import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { Plus, Trash2, FileText, FileSpreadsheet, Upload, Download, AlertCircle } from 'lucide-react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { PACKAGE_TYPE_OPTIONS, downloadPackingTemplate } from './utils/excelTemplates';
import { buildDocumentWorkbook, buildExcelFilename, normalizeOfferType } from './utils/offerWorkbook';
import finMarineLogo from './fin-marine-logo.png';
import finMarineIntlLogo from './FİN MARİNE İNTERNATİONAL FZC LOGO YENİ copy.png';
import atlantisLogo from './Atlantis marine power solution logo-1.png';
import './index.css';

// Accept strings (legacy saved offers) OR arrays, always return a non-empty array.
const normalizeTermsField = (value, fallback = '') => {
  if (Array.isArray(value)) {
    const cleaned = value.map((v) => (v == null ? '' : String(v)));
    return cleaned.length ? cleaned : [fallback];
  }
  if (typeof value === 'string') return [value];
  if (value == null) return [fallback];
  return [String(value)];
};

const normalizeTerms = (terms) => ({
  deliveryTerms: normalizeTermsField(terms?.deliveryTerms, 'Ex Works Turkey'),
  deliveryTime: normalizeTermsField(terms?.deliveryTime, 'Ready for delivery'),
  paymentTerms: normalizeTermsField(terms?.paymentTerms, '100% Bank transfer before delivery'),
});

const MarineOfferBuilder = forwardRef(({ initialData, onDataChange, onSendToFinanceClick, canSendToFinance = false }, ref) => {
  const [darkMode, setDarkMode] = useState(false);
  const [currentOffer, setCurrentOffer] = useState(() => {
    const base = initialData || {
    company: 'fin-marine',
    bank: 'qnb',
    offerType: 'OFFER',
    offerNumber: '',
    date: new Date().toISOString().split('T')[0],
    customer: {
      name: '',
      address: '',
      contact: ''
    },
    description: '',
    engineSerialNumber: '',
    currency: 'USD',
    items: [
      {
        id: 1,
        description: '',
        partNo: '',
        qty: 1,
        unitPrice: 0,
        totalPrice: 0,
        currency: 'USD',
        condition: 'NEW',
        coo: 'none',
        cooOther: '',
        make: 'none',
        makeOther: '',
        remarks: '',
        availability: '',
        weight: 0,
        packageNumber: 'Box 1'
      }
    ],
    terms: {
      deliveryTerms: ['Ex Works Turkey'],
      deliveryTime: ['Ready for delivery'],
      paymentTerms: ['100% Bank transfer before delivery']
    },
    bankDetails: {
      currency: 'USD',
      holder: '',
      bank: '',
      branch: '',
      branchCode: '',
      accountNo: '',
      iban: '',
      swiftCode: '',
      routingCode: ''
    },
      // Discount fields
      discountEnabled: false,
      discountType: 'percentage', // 'percentage' or 'amount'
      discountValue: 0,
      discountAmount: 0,
      discountLabel: 'discount', // 'discount' or 'paid'
      priceMultiplierPercent: 0
    };
    return { ...base, terms: normalizeTerms(base.terms) };
  });

  // Packing List specific states
  const [packages, setPackages] = useState(
    initialData?.packages && Array.isArray(initialData.packages) 
      ? initialData.packages.map(pkg => ({
          ...pkg,
          boxWeight: pkg.boxWeight || 0,
          length: pkg.dimensions?.length || pkg.length || 0,
          width: pkg.dimensions?.width || pkg.width || 0,
          height: pkg.dimensions?.height || pkg.height || 0
        }))
      : [{
          id: 1,
          number: 'Box 1',
          type: 'Wooden Box',
          length: 0,
          width: 0,
          height: 0,
          boxWeight: 0
        }]
  );
  const [pickupAddress, setPickupAddress] = useState(initialData?.pickupAddress || '');
  const [deliveryAddress, setDeliveryAddress] = useState(initialData?.deliveryAddress || '');
  const [consigneeAddress, setConsigneeAddress] = useState(initialData?.consigneeAddress || '');
  const [showConsignee, setShowConsignee] = useState(initialData?.showConsignee || false);
  const [weightUnit, setWeightUnit] = useState(initialData?.weightUnit || 'KG'); // KG or LBS

  // Packing List Excel/CSV import
  const packingFileInputRef = useRef(null);
  const [packingImportLoading, setPackingImportLoading] = useState(false);
  const [packingImportError, setPackingImportError] = useState('');
  const [packingImportSuccess, setPackingImportSuccess] = useState('');
  
  // Optional sections for non-packing list documents
  const [offerPickupAddress, setOfferPickupAddress] = useState(initialData?.offerPickupAddress || '');
  const [offerShippingAddress, setOfferShippingAddress] = useState(initialData?.offerShippingAddress || '');
  const [offerComments, setOfferComments] = useState(initialData?.offerComments || '');
  const [showOfferAddresses, setShowOfferAddresses] = useState(initialData?.showOfferAddresses || false);
  const [showOfferComments, setShowOfferComments] = useState(initialData?.showOfferComments || false);



  // Read at save time. This is not copied onto a ref during render, and the
  // parent header is updated on a short delay rather than on every keystroke.
  const readLiveOffer = () => (currentOffer.items ? {
    ...currentOffer,
    packages: currentOffer.offerType === 'PACKING LIST' ? packages.map(pkg => ({
      ...pkg,
      dimensions: { length: pkg.length, width: pkg.width, height: pkg.height }
    })) : undefined,
    pickupAddress: currentOffer.offerType === 'PACKING LIST' ? pickupAddress : undefined,
    deliveryAddress: currentOffer.offerType === 'PACKING LIST' ? deliveryAddress : undefined,
    consigneeAddress: currentOffer.offerType === 'PACKING LIST' && showConsignee ? consigneeAddress : undefined,
    showConsignee: currentOffer.offerType === 'PACKING LIST' ? showConsignee : undefined,
    weightUnit: currentOffer.offerType === 'PACKING LIST' ? weightUnit : undefined,
    offerPickupAddress: currentOffer.offerType !== 'PACKING LIST' && showOfferAddresses ? offerPickupAddress : undefined,
    offerShippingAddress: currentOffer.offerType !== 'PACKING LIST' && showOfferAddresses ? offerShippingAddress : undefined,
    offerComments: currentOffer.offerType !== 'PACKING LIST' && showOfferComments ? offerComments : undefined,
    showOfferAddresses: currentOffer.offerType !== 'PACKING LIST' ? showOfferAddresses : undefined,
    showOfferComments: currentOffer.offerType !== 'PACKING LIST' ? showOfferComments : undefined
  } : null);

  useImperativeHandle(ref, () => ({
    getOffer: () => readLiveOffer()
  }));

  useEffect(() => {
    const offer = readLiveOffer();
    if (!onDataChange || !offer) return undefined;
    const timer = setTimeout(() => onDataChange(offer), 300);
    return () => clearTimeout(timer);
  }, [currentOffer, packages, pickupAddress, deliveryAddress, consigneeAddress, showConsignee, weightUnit, offerPickupAddress, offerShippingAddress, offerComments, showOfferAddresses, showOfferComments]); // Remove onDataChange from dependencies

  const companies = {
    'fin-marine': {
      name: 'FIN MARINE & ENERGY SOLUTIONS LTD.',
      address: 'Trust Company Complex, Ajeltake Road, Ajeltake Island, Majuro, Republic of the Marshall Islands MH 96960',
      email: 'info@fin-marine.com',
      logo: '🌊 FIN MARINE',
      banks: {
        'qnb': {
          label: 'QNB Bank',
          accounts: {
            USD: {
              holder: 'Fin Marine & Energy Solutions Ltd',
              bank: 'QNB Bank',
              branch: 'TUZLA, İstanbul',
              branchCode: '00885',
              iban: 'TR47 0011 1000 0000 0109 0368 77',
              swiftCode: 'FNNBTRISOPS'
            },
            EUR: {
              holder: 'Fin Marine & Energy Solutions Ltd',
              bank: 'QNB Bank',
              branch: 'TUZLA, İstanbul',
              branchCode: '00885',
              iban: 'TR13 0011 1000 0000 0109 0409 81',
              swiftCode: 'FNNBTRISOPS'
            },
            GBP: {
              holder: 'Fin Marine & Energy Solutions Ltd',
              bank: 'QNB Bank',
              branch: 'TUZLA, İstanbul',
              branchCode: '00885',
              iban: 'TR03 0011 1000 0000 0109 0372 81',
              swiftCode: 'FNNBTRISOPS'
            }
          }
        }
      }
    },
    'fin-marine-fzco': {
      name: 'FIN MARINE AND ENERGY SOLUTIONS INTERNATIONAL FZCO',
      address: 'HD 140 Floor 23 Sheikh Rashid Tower, Dubai World Trade Centre, Dubai, UAE',
      email: 'info@fin-marine.com',
      logo: '🌊 FIN MARINE INTL',
      banks: {
        'mashreq': {
          label: 'Mashreq Bank',
          accounts: {
            USD: {
              holder: 'FIN MARINE AND ENERGY SOLUTIONS INTERNATIONAL FZCO',
              bank: 'Mashreq bank',
              branch: 'ABU DHABI MAIN',
              accountNo: '019101210689',
              iban: 'AE850330000019101210689',
              swiftCode: 'BOMLAEAD'
            },
            EUR: {
              holder: 'FIN MARINE AND ENERGY SOLUTIONS INTERNATIONAL FZCO',
              bank: 'Mashreq bank',
              branch: 'ABU DHABI MAIN',
              accountNo: '019101436645',
              iban: 'AE880330000019101436645',
              swiftCode: 'BOMLAEAD'
            }
          }
        }
      }
    },
    'atlantis-marine': {
      name: 'ATLANTIS MARINE AND POWER SOLUTION FZ-LLC',
      address: 'S32-16 Shed No.32 - TP Al Hamra FZ, Al Hamra Industrial Zone-FZ, RAK, United Arab Emirates',
      email: 'kerem@atlantismarinepower.com',
      logo: '🌊 ATLANTIS',
      banks: {
        'dubai-islamic': {
          label: 'Dubai Islamic Bank',
          accounts: {
            USD: {
              holder: 'Atlantis Marine and Power Solution Fz LLC',
              bank: 'Dubai Islamic Bank',
              branch: 'Sheikh Zayed Road Branch 2',
              branchCode: '097',
              accountNo: '097525227663901',
              iban: 'AE690240097525227663901',
              swiftCode: 'DUIBAEAD'
            },
            AED: {
              holder: 'Atlantis Marine and Power Solution Fz LLC',
              bank: 'Dubai Islamic Bank',
              branch: 'Sheikh Zayed Road Branch 2',
              branchCode: '097',
              accountNo: '097524227663901',
              iban: 'AE170240097524227663901',
              swiftCode: 'DUIBAEAD'
            }
          }
        },
        'emirates-nbd': {
          label: 'Emirates NBD',
          accounts: {
            USD: {
              holder: 'Atlantis Marine and Power Solution FZ-LLC',
              bank: 'Emirates NBD',
              branch: 'Al Barsha Branch',
              accountNo: '1025969511103',
              iban: 'AE160260001025969511103',
              swiftCode: 'EBILAEAD',
              routingCode: '302620122'
            },
            EUR: {
              holder: 'Atlantis Marine and Power Solution FZ-LLC',
              bank: 'Emirates NBD',
              branch: 'Al Barsha Branch',
              accountNo: '1025969511102',
              iban: 'AE430260001025969511102',
              swiftCode: 'EBILAEAD',
              routingCode: '302620122'
            },
            GBP: {
              holder: 'Atlantis Marine and Power Solution FZ-LLC',
              bank: 'Emirates NBD',
              branch: 'Al Barsha Branch',
              accountNo: '1025969511104',
              iban: 'AE860260001025969511104',
              swiftCode: 'EBILAEAD',
              routingCode: '302620122'
            },
            AED: {
              holder: 'Atlantis Marine and Power Solution FZ-LLC',
              bank: 'Emirates NBD',
              branch: 'Al Barsha Branch',
              accountNo: '1015969511101',
              iban: 'AE350260001015969511101',
              swiftCode: 'EBILAEAD',
              routingCode: '302620122'
            }
          }
        }
      }
    }
  };

  // Auto-generate offer number based on company and date
  useEffect(() => {
    if (!currentOffer.offerNumber) {
      const date = new Date(currentOffer.date);
      const year = date.getFullYear().toString().slice(-2);
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const companyCode = currentOffer.company === 'fin-marine' ? '' : 'AT';
      const offerNum = `${year}${month}${day}${companyCode}`;
      
      setCurrentOffer(prev => ({
        ...prev,
        offerNumber: offerNum
      }));
    }
  }, [currentOffer.date, currentOffer.company]);

  // Update bank details when company, bank, or currency changes.
  // Also normalizes `bank` (when missing or not valid for the company) and
  // `currency` (when not supported by the selected bank) so older saved
  // offers and company switches end up in a consistent state.
  useEffect(() => {
    const company = companies[currentOffer.company];
    if (!company || !company.banks) return;

    const availableBankKeys = Object.keys(company.banks);
    if (availableBankKeys.length === 0) return;

    const desiredBankKey = company.banks[currentOffer.bank]
      ? currentOffer.bank
      : availableBankKeys[0];
    const bank = company.banks[desiredBankKey];
    if (!bank || !bank.accounts) return;

    const availableCurrencies = Object.keys(bank.accounts);
    if (availableCurrencies.length === 0) return;

    const desiredCurrency = bank.accounts[currentOffer.currency]
      ? currentOffer.currency
      : availableCurrencies[0];
    const accountDetails = bank.accounts[desiredCurrency];
    if (!accountDetails) return;

    setCurrentOffer(prev => {
      // Avoid unnecessary state updates that would trigger re-render loops.
      const sameBank = prev.bank === desiredBankKey;
      const sameCurrency = prev.currency === desiredCurrency;
      const sameDetails = prev.bankDetails
        && prev.bankDetails.holder === accountDetails.holder
        && prev.bankDetails.bank === accountDetails.bank
        && prev.bankDetails.iban === accountDetails.iban
        && prev.bankDetails.swiftCode === accountDetails.swiftCode
        && prev.bankDetails.accountNo === (accountDetails.accountNo || '')
        && prev.bankDetails.branch === (accountDetails.branch || '')
        && prev.bankDetails.branchCode === (accountDetails.branchCode || '')
        && prev.bankDetails.routingCode === (accountDetails.routingCode || '');

      if (sameBank && sameCurrency && sameDetails) {
        return prev;
      }

      return {
        ...prev,
        bank: desiredBankKey,
        currency: desiredCurrency,
        bankDetails: {
          ...prev.bankDetails,
          // Reset optional fields so values from a previously selected bank
          // do not leak into the newly selected one.
          branch: '',
          branchCode: '',
          accountNo: '',
          routingCode: '',
          ...accountDetails
        }
      };
    });
  }, [currentOffer.company, currentOffer.bank, currentOffer.currency]);

  // Handle loading saved offer data - removed to prevent loops
  // The component will re-mount with new key when data changes

  // Update discount amount when relevant values change
  useEffect(() => {
    if (currentOffer.discountEnabled) {
      const subtotal = getSubtotal();
      const discountAmount = calculateDiscountAmount(subtotal, currentOffer.discountType, currentOffer.discountValue);
      
      if (discountAmount !== currentOffer.discountAmount) {
        setCurrentOffer(prev => ({
          ...prev,
          discountAmount: discountAmount
        }));
      }
    }
  }, [currentOffer.items, currentOffer.discountType, currentOffer.discountValue, currentOffer.discountEnabled, currentOffer.priceMultiplierPercent, currentOffer.offerType]);

  // Calculate totals
  const calculateItemTotal = (qty, unitPrice) => {
    // Ensure inputs are numbers and round to avoid floating point issues
    const quantity = parseFloat(qty) || 0;
    const price = parseFloat(unitPrice) || 0;
    const total = quantity * price;
    // Round to 2 decimal places to avoid floating point precision issues
    return Math.round(total * 100) / 100;
  };

  // Calculate discount amount
  const calculateDiscountAmount = (subtotal, discountType, discountValue) => {
    if (!discountValue || discountValue <= 0) return 0;
    
    if (discountType === 'percentage') {
      const percentage = Math.min(discountValue, 100); // Cap at 100%
      return (subtotal * percentage) / 100;
    } else {
      return Math.min(discountValue, subtotal); // Can't discount more than total
    }
  };

  const multiplierEligibleTypes = ['OFFER', 'PROFORMA INVOICE', 'INVOICE', 'COMMERCIAL INVOICE'];
  const isMultiplierApplicable = () => multiplierEligibleTypes.includes(currentOffer.offerType);

  // Country of Origin (COO) and Make are supported for these document types only
  const cooMakeEligibleTypes = ['OFFER', 'PROFORMA INVOICE', 'INVOICE', 'COMMERCIAL INVOICE', 'PACKING LIST'];
  const isCOOMakeApplicable = (type = currentOffer.offerType) => cooMakeEligibleTypes.includes(type);

  // Resolve display value for an item's COO ('none' or unset -> empty string)
  const getCOODisplay = (item) => {
    const value = item?.coo || 'none';
    if (value === 'none') return '';
    if (value === 'EU') return 'EU Maker';
    if (value === 'NON_EU') return 'Non-EU Maker';
    if (value === 'OTHER') return (item?.cooOther || '').trim();
    return '';
  };

  // Resolve display value for an item's Make ('none' or unset -> empty string)
  const getMakeDisplay = (item) => {
    const value = item?.make || 'none';
    if (value === 'none') return '';
    if (value === 'OTHER') return (item?.makeOther || '').trim();
    return value;
  };

  // Whether a given column should be rendered for the current document.
  // Hidden when the doc type doesn't support it OR when every item is set to 'none'.
  const shouldShowCOOColumn = () => isCOOMakeApplicable() && currentOffer.items.some(it => (it?.coo || 'none') !== 'none');
  const shouldShowMakeColumn = () => isCOOMakeApplicable() && currentOffer.items.some(it => (it?.make || 'none') !== 'none');
  const getMultiplierPercent = () => (isMultiplierApplicable() ? Math.max(0, parseFloat(currentOffer.priceMultiplierPercent) || 0) : 0);
  const getMultiplierFactor = () => 1 + (getMultiplierPercent() / 100);
  const getAdjustedUnitPrice = (item) => (parseFloat(item?.unitPrice) || 0) * getMultiplierFactor();
  const getAdjustedTotalPrice = (item) => calculateItemTotal(item?.qty, getAdjustedUnitPrice(item));

  // Get subtotal (before discount)
  const getSubtotal = () => {
    return currentOffer.items.reduce((total, item) => total + getAdjustedTotalPrice(item), 0);
  };

  // Get final total (after discount)
  const getFinalTotal = () => {
    const subtotal = getSubtotal();
    const discountAmount = currentOffer.discountEnabled 
      ? calculateDiscountAmount(subtotal, currentOffer.discountType, currentOffer.discountValue)
      : 0;
    return subtotal - discountAmount;
  };

  // For Purchase Orders each item carries its own currency. Compute a total
  // per currency and return an ordered list (preserving the order each
  // currency first appears in the items list).
  const getPurchaseOrderTotalsByCurrency = () => {
    const totals = new Map();
    (currentOffer.items || []).forEach(item => {
      const currency = item.currency || 'USD';
      const amount = parseFloat(item.totalPrice) || 0;
      totals.set(currency, (totals.get(currency) || 0) + amount);
    });
    return Array.from(totals.entries()).map(([currency, amount]) => ({ currency, amount }));
  };

  const updateItem = (index, field, value) => {
    const newItems = [...currentOffer.items];
    
    // Round numeric values to avoid floating point issues
    let processedValue = value;
    if (field === 'unitPrice' && typeof value === 'number') {
      processedValue = Math.round(value * 100) / 100;
    }
    
    newItems[index] = { ...newItems[index], [field]: processedValue };
    
    if (field === 'qty' || field === 'unitPrice') {
      newItems[index].totalPrice = calculateItemTotal(
        field === 'qty' ? processedValue : newItems[index].qty,
        field === 'unitPrice' ? processedValue : newItems[index].unitPrice
      );
    }
    
    setCurrentOffer(prev => ({ ...prev, items: newItems }));
  };

  const addItem = () => {
    const newItem = {
      id: Date.now(),
      description: '',
      partNo: '',
      qty: 1,
      unitPrice: 0,
      totalPrice: 0,
      currency: 'USD',
      condition: 'NEW',
      coo: 'none',
      cooOther: '',
      make: 'none',
      makeOther: '',
      remarks: '',
      availability: '',
      weight: 0,
      packageNumber: 'Box 1'
    };
    setCurrentOffer(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));
  };

  const removeItem = (index) => {
    setCurrentOffer(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  // ---- Packing List import from Excel/CSV ----

  // Map header cells to item fields in two passes: strong, unambiguous headers
  // first (e.g. "Spare Part No"), weaker fallbacks after — so a serial column
  // like "Item No" doesn't steal the part-number slot when a real part column
  // exists on the same row.
  const detectPackingColumns = (headerCells) => {
    const texts = headerCells.map(cell => String(cell || '').toLowerCase().trim());
    const cols = { serial: -1, description: -1, partNo: -1, qty: -1, weight: -1, packageNumber: -1, remarks: -1, condition: -1 };

    texts.forEach((text, idx) => {
      if (!text) return;
      if (cols.serial === -1 && /^(item\s*no\.?|s\/?n\.?|sl\.?\s*no\.?|sr\.?\s*no\.?|serial(\s*(no|number))?\.?|no\.?|#)$/.test(text)) {
        cols.serial = idx;
      } else if (cols.partNo === -1 && /(part|p\/n|article|spare)/.test(text) && /(no|number|code|#|p\/n)/.test(text)) {
        cols.partNo = idx;
      } else if (cols.description === -1 && /description/.test(text)) {
        cols.description = idx;
      } else if (cols.qty === -1 && /(qty|quantity|pcs|pieces)/.test(text)) {
        cols.qty = idx;
      } else if (cols.weight === -1 && /(weight|\bkgs?\b|\blbs\b)/.test(text) && !/(box|package|gross|total)/.test(text)) {
        cols.weight = idx;
      } else if (cols.packageNumber === -1 && /(package|box|case|pallet|crate|pkg|carton)/.test(text)) {
        cols.packageNumber = idx;
      } else if (cols.remarks === -1 && /(remark|note|comment)/.test(text)) {
        cols.remarks = idx;
      } else if (cols.condition === -1 && /condition/.test(text)) {
        cols.condition = idx;
      }
    });

    // Weaker fallbacks for anything still unmapped
    texts.forEach((text, idx) => {
      if (!text || Object.values(cols).includes(idx)) return;
      if (cols.partNo === -1 && /(item\s*(no|number|code)|\bpn\b|ref(erence)?\s*(no|number|#))/.test(text)) {
        cols.partNo = idx;
      } else if (cols.description === -1 && /(item|goods|product|material|scope)/.test(text)) {
        cols.description = idx;
      } else if (cols.qty === -1 && /amount/.test(text)) {
        cols.qty = idx;
      }
    });

    return cols;
  };

  // Handles both "1,234.56" and European "528,00" / "1.234,56" number styles
  const parseDecimalNumber = (raw) => {
    let str = String(raw ?? '').replace(/[^\d.,-]/g, '');
    if (!str) return NaN;
    const lastComma = str.lastIndexOf(',');
    const lastDot = str.lastIndexOf('.');
    if (lastComma !== -1 && lastDot !== -1) {
      str = lastComma > lastDot
        ? str.replace(/\./g, '').replace(/,/g, '.')
        : str.replace(/,/g, '');
    } else if (lastComma !== -1) {
      const decimals = str.length - lastComma - 1;
      str = (decimals > 0 && decimals <= 2 && str.indexOf(',') === lastComma)
        ? str.replace(',', '.')
        : str.replace(/,/g, '');
    }
    return parseFloat(str);
  };

  const parsePackingNumber = (value) => {
    if (value == null || value === '') return 0;
    if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
    const num = parseDecimalNumber(value);
    return Number.isFinite(num) ? num : 0;
  };

  // "1" / "01" -> "Box 1"; anything else is kept as typed (e.g. "Pallet A")
  const normalizePackageLabel = (value) => {
    const text = String(value ?? '').trim();
    if (!text) return '';
    if (/^\d+(\.0+)?$/.test(text)) return `Box ${parseInt(text, 10)}`;
    return text;
  };

  const normalizeImportedCondition = (value) => {
    const text = String(value ?? '').toLowerCase();
    if (text.includes('recond')) return 'RECONDITIONED';
    if (text.includes('used') || text.includes('second')) return 'USED';
    return 'NEW';
  };

  // Pull box dimensions and gross weight out of free text like
  // "W x L x H    900mm x 1100mm x 970mm    528,00 Kg" (converted to cm)
  const parseDimensionsAndWeight = (text) => {
    const result = {};
    const str = String(text || '');

    const dimMatch = str.match(/(\d+(?:[.,]\d+)?)\s*(mm|cm|m)?\s*[x×*]\s*(\d+(?:[.,]\d+)?)\s*(mm|cm|m)?\s*[x×*]\s*(\d+(?:[.,]\d+)?)\s*(mm|cm|m)?/i);
    if (dimMatch) {
      const unit = (dimMatch[6] || dimMatch[4] || dimMatch[2] || 'cm').toLowerCase();
      const toCm = unit === 'mm' ? 0.1 : unit === 'm' ? 100 : 1;
      const nums = [dimMatch[1], dimMatch[3], dimMatch[5]].map(n => Math.round(parseDecimalNumber(n) * toCm * 10) / 10);

      // Respect an explicit axis order like "W x L x H"; default is L x W x H
      const orderMatch = str.match(/\b([lwh])\s*[x×*]\s*([lwh])\s*[x×*]\s*([lwh])\b/i);
      const axes = orderMatch
        ? [orderMatch[1], orderMatch[2], orderMatch[3]].map(a => a.toLowerCase())
        : ['l', 'w', 'h'];
      const axisKey = { l: 'length', w: 'width', h: 'height' };
      const dimensions = {};
      axes.forEach((axis, i) => { dimensions[axisKey[axis]] = nums[i]; });
      if (dimensions.length != null && dimensions.width != null && dimensions.height != null) {
        result.dimensions = dimensions;
      }
    }

    const weightMatch = str.match(/(\d+(?:[.,]\d+)?)\s*(kg|kgs|lbs)\b/i);
    if (weightMatch) result.weight = parsePackingNumber(weightMatch[1]);

    return result;
  };

  const inferPackageType = (label) => {
    const text = String(label || '').toLowerCase();
    if (text.includes('pallet')) return 'Pallet';
    if (text.includes('crate')) return 'Wooden Crate';
    if (text.includes('carton') || text.includes('cardboard')) return 'Cardboard Box';
    if (text.includes('container')) return 'Container';
    return 'Wooden Box';
  };

  const boxLabelPattern = /^(box|pallet|crate|carton|case|package)[\s.:#-]*(no\.?|number|#)?\s*[\w-]*$/i;

  const normalizeBoxKey = (label) => String(label || '').toLowerCase().replace(/\s+/g, ' ').trim();

  const findPackingHeader = (rows) => {
    for (let i = 0; i < Math.min(rows.length, 20); i++) {
      const row = rows[i];
      if (!Array.isArray(row)) continue;
      const cols = detectPackingColumns(row);
      if (cols.description !== -1 || (cols.partNo !== -1 && cols.qty !== -1)) {
        return { index: i, cols };
      }
    }
    return null;
  };

  const extractPackingItems = (rows, startIdx, cols) => {
    const items = [];
    for (let i = startIdx; i < rows.length; i++) {
      const row = rows[i];
      if (!Array.isArray(row) || row.length === 0) continue;

      const description = cols.description !== -1 ? String(row[cols.description] ?? '').trim() : '';
      const partNo = cols.partNo !== -1 ? String(row[cols.partNo] ?? '').trim() : '';
      if (!description && !partNo) continue;
      if (/^(total|subtotal|grand\s*total|sum)\b/i.test(description)) continue;

      items.push({
        description,
        partNo,
        qty: cols.qty !== -1 ? (parsePackingNumber(row[cols.qty]) || 1) : 1,
        weight: cols.weight !== -1 ? parsePackingNumber(row[cols.weight]) : 0,
        packageNumber: cols.packageNumber !== -1 ? normalizePackageLabel(row[cols.packageNumber]) : '',
        remarks: cols.remarks !== -1 ? String(row[cols.remarks] ?? '').trim() : '',
        condition: cols.condition !== -1 ? normalizeImportedCondition(row[cols.condition]) : 'NEW'
      });
    }
    return items;
  };

  // Name the box for a sheet: a cell like "BOX NO 1" above the item table wins,
  // then a box-like sheet name; with several item sheets each sheet is its own
  // box even under a generic name.
  const findBoxLabel = (sheetName, rows, headerIdx, isOnlyItemSheet) => {
    for (let i = 0; i < headerIdx; i++) {
      const row = rows[i];
      if (!Array.isArray(row)) continue;
      for (const cell of row) {
        const text = String(cell ?? '').replace(/\s+/g, ' ').trim();
        if (text && text.length <= 40 && boxLabelPattern.test(text)) return text;
      }
    }
    const name = String(sheetName || '').replace(/\s+/g, ' ').trim();
    if (/(box|pallet|crate|carton|case|pkg|package)/i.test(name)) return name;
    if (!isOnlyItemSheet) return name;
    return '';
  };

  // A sheet without an item table may still describe the boxes, e.g. a
  // "BOX CONFIGURATION" summary:
  // "BOX NO 1 | Wooden Box | 900mm x 1100mm x 970mm | 528,00 Kg"
  const collectBoxConfiguration = (rows, configMap) => {
    rows.forEach(row => {
      if (!Array.isArray(row)) return;
      const rowText = row.map(cell => String(cell ?? '')).join('  ');
      const info = parseDimensionsAndWeight(rowText);

      const labelCell = row.find(cell => {
        const text = String(cell ?? '').replace(/\s+/g, ' ').trim();
        return text && text.length <= 40 && boxLabelPattern.test(text);
      });
      if (!labelCell) return;

      // A cell that exactly matches one of the known package types (e.g. the
      // template's Type dropdown column) sets the box type
      const typeCell = row.find(cell => {
        if (cell === labelCell) return false;
        const text = String(cell ?? '').replace(/\s+/g, ' ').trim().toLowerCase();
        return PACKAGE_TYPE_OPTIONS.some(option => option.toLowerCase() === text);
      });
      if (typeCell) {
        const text = String(typeCell).replace(/\s+/g, ' ').trim().toLowerCase();
        info.type = PACKAGE_TYPE_OPTIONS.find(option => option.toLowerCase() === text);
      }

      if (!info.dimensions && info.weight == null && !info.type) return;

      const key = normalizeBoxKey(labelCell);
      configMap.set(key, { ...(configMap.get(key) || {}), ...info });
    });
  };

  // Pull pickup/delivery/consignee addresses out of rows like
  // "PICKUP ADDRESS | Sender company, street, city" (the address may follow
  // a colon in the same cell, sit in the next cells, or be on the next row)
  const addressLabelPatterns = [
    { key: 'pickup', regex: /^pick[\s-]*up\s*address\b/i },
    { key: 'delivery', regex: /^delivery\s*address\b/i },
    { key: 'consignee', regex: /^consignee(\s*address)?\b/i }
  ];

  const collectAddresses = (rows, addresses) => {
    rows.forEach((row, rowIdx) => {
      if (!Array.isArray(row)) return;
      row.forEach((cell, colIdx) => {
        const text = String(cell ?? '').trim();
        if (!text) return;
        const match = addressLabelPatterns.find(p => p.regex.test(text));
        if (!match || addresses[match.key]) return;

        let value = text.replace(match.regex, '').replace(/^[\s:—–-]+/, '').trim();
        if (!value) {
          value = row.slice(colIdx + 1).map(c => String(c ?? '').trim()).filter(Boolean).join(', ');
        }
        if (!value) {
          const nextRow = rows[rowIdx + 1];
          if (Array.isArray(nextRow)) {
            value = nextRow.map(c => String(c ?? '').trim()).filter(Boolean).join(', ');
          }
        }
        if (value) addresses[match.key] = value;
      });
    });
  };

  const applyImportedPackingItems = (parsedItems, importedBoxes = []) => {
    const timestamp = Date.now();
    const newItems = parsedItems.map((item, idx) => ({
      id: timestamp + idx,
      description: item.description,
      partNo: item.partNo,
      qty: item.qty,
      unitPrice: 0,
      totalPrice: 0,
      currency: 'USD',
      condition: item.condition,
      coo: 'none',
      cooOther: '',
      make: 'none',
      makeOther: '',
      remarks: item.remarks,
      availability: '',
      weight: item.weight,
      packageNumber: item.packageNumber || 'Box 1'
    }));

    // If the current list is only untouched blank rows, replace it (and start
    // the package list fresh); otherwise append to what's already there.
    const replacing = currentOffer.items.every(
      it => !String(it.description || '').trim() && !String(it.partNo || '').trim()
    );

    setCurrentOffer(prev => ({
      ...prev,
      items: replacing ? newItems : [...prev.items, ...newItems]
    }));

    setPackages(prev => {
      const result = replacing ? [] : [...prev];

      const upsert = (number, info = {}) => {
        if (!number) return;
        const idx = result.findIndex(pkg => pkg.number === number);
        if (idx === -1) {
          result.push({
            id: timestamp + 100000 + result.length,
            number,
            type: info.type || inferPackageType(number),
            length: info.dimensions?.length || 0,
            width: info.dimensions?.width || 0,
            height: info.dimensions?.height || 0,
            boxWeight: info.boxWeight || 0
          });
        } else if (info.dimensions || info.boxWeight || info.type) {
          result[idx] = {
            ...result[idx],
            type: info.type || result[idx].type,
            length: info.dimensions?.length ?? result[idx].length,
            width: info.dimensions?.width ?? result[idx].width,
            height: info.dimensions?.height ?? result[idx].height,
            boxWeight: info.boxWeight || result[idx].boxWeight
          };
        }
      };

      importedBoxes.forEach(box => upsert(box.number, box));
      newItems.forEach(item => upsert(item.packageNumber));

      return result.length ? result : prev;
    });

    return newItems.length;
  };

  const handlePackingFileUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow picking the same file again
    if (!file) return;

    setPackingImportLoading(true);
    setPackingImportError('');
    setPackingImportSuccess('');

    try {
      const data = new Uint8Array(await file.arrayBuffer());
      const workbook = XLSX.read(data, { type: 'array' });

      // Sheets with an item table are boxes; the rest may hold box
      // configuration (dimensions/weights/types per box) and addresses.
      const itemSheets = [];
      const configMap = new Map();
      const addresses = {};

      workbook.SheetNames.forEach(sheetName => {
        const worksheet = workbook.Sheets[sheetName];
        if (!worksheet) return;
        // raw: false keeps number formats, so a gross weight stored as the
        // number 528 with a '#,##0.00" Kg"' format arrives as "528.00 Kg"
        // and the kg-suffix weight detection can see it
        const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '', raw: false });
        const header = findPackingHeader(rows);
        if (header) {
          itemSheets.push({ sheetName, rows, header });
        } else {
          collectBoxConfiguration(rows, configMap);
          collectAddresses(rows, addresses);
        }
      });

      const parsedItems = [];
      const importedBoxes = [];

      if (itemSheets.length > 0) {
        const isOnlyItemSheet = itemSheets.length === 1;

        itemSheets.forEach(({ sheetName, rows, header }) => {
          const boxLabel = findBoxLabel(sheetName, rows, header.index, isOnlyItemSheet);
          const sheetItems = extractPackingItems(rows, header.index + 1, header.cols)
            .map(item => ({ ...item, packageNumber: item.packageNumber || boxLabel }));
          if (sheetItems.length === 0) return;

          parsedItems.push(...sheetItems);

          if (boxLabel) {
            // Dimensions/weight are usually written above the item table;
            // fall back to the configuration sheet when they're not.
            const headText = rows
              .slice(0, header.index)
              .map(row => (Array.isArray(row) ? row.map(cell => String(cell ?? '')).join('  ') : ''))
              .join('\n');
            const info = parseDimensionsAndWeight(headText);
            const config = configMap.get(normalizeBoxKey(boxLabel)) || {};

            importedBoxes.push({
              number: boxLabel,
              // Only carry an explicit type (from the configuration sheet) so
              // merging into an existing package never clobbers a user's
              // choice with a guess; creation falls back to inference.
              type: config.type || null,
              dimensions: info.dimensions || config.dimensions || null,
              boxWeight: info.weight ?? config.weight ?? 0
            });
          }
        });
      } else if (workbook.SheetNames.length > 0) {
        // No recognizable header anywhere: treat the first column of the first
        // sheet as descriptions
        const rows = XLSX.utils.sheet_to_json(workbook.Sheets[workbook.SheetNames[0]], { header: 1, defval: '' });
        const fallbackCols = { serial: -1, description: 0, partNo: -1, qty: -1, weight: -1, packageNumber: -1, remarks: -1, condition: -1 };
        parsedItems.push(...extractPackingItems(rows, 0, fallbackCols));
      }

      if (parsedItems.length === 0) {
        setPackingImportError('No items found in the file. Make sure it has columns like Description, Part No, Qty, Weight and Package — or one sheet per box.');
        return;
      }

      const importedCount = applyImportedPackingItems(parsedItems, importedBoxes);

      // Fill address fields when the file provides them (never clears
      // anything the user already typed)
      if (addresses.pickup) setPickupAddress(addresses.pickup);
      if (addresses.delivery) setDeliveryAddress(addresses.delivery);
      if (addresses.consignee) {
        setConsigneeAddress(addresses.consignee);
        setShowConsignee(true);
      }

      const boxCount = importedBoxes.length;
      setPackingImportSuccess(
        boxCount > 0
          ? `Imported ${importedCount} item${importedCount === 1 ? '' : 's'} in ${boxCount} box${boxCount === 1 ? '' : 'es'} from "${file.name}".`
          : `Imported ${importedCount} item${importedCount === 1 ? '' : 's'} from "${file.name}".`
      );
    } catch (err) {
      console.error('Packing list import error:', err);
      setPackingImportError('Could not read the file. Please use a valid Excel (.xlsx / .xls) or CSV file.');
    } finally {
      setPackingImportLoading(false);
    }
  };

  // Terms & Conditions: array-based repeatable inputs
  const updateTermsEntry = (field, index, value) => {
    setCurrentOffer(prev => {
      const current = Array.isArray(prev.terms?.[field]) ? prev.terms[field] : [];
      const next = current.slice();
      next[index] = value;
      return { ...prev, terms: { ...prev.terms, [field]: next } };
    });
  };

  const addTermsEntry = (field) => {
    setCurrentOffer(prev => {
      const current = Array.isArray(prev.terms?.[field]) ? prev.terms[field] : [];
      return { ...prev, terms: { ...prev.terms, [field]: [...current, ''] } };
    });
  };

  const removeTermsEntry = (field, index) => {
    setCurrentOffer(prev => {
      const current = Array.isArray(prev.terms?.[field]) ? prev.terms[field] : [];
      if (current.length <= 1) return prev;
      return {
        ...prev,
        terms: { ...prev.terms, [field]: current.filter((_, i) => i !== index) },
      };
    });
  };

  const getGrandTotal = () => {
    return getSubtotal();
  };

  const formatCurrency = (amount, currency = currentOffer.currency) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  // Helper function to format price with thousands separators and 2 decimal places
  const formatPrice = (price) => {
    const num = parseFloat(price || 0);
    return num.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  // Dark mode class helper
  const dm = (lightClass, darkClass) => darkMode ? darkClass : lightClass;
  
  // Common input classes
  const inputClass = `w-full p-4 border-2 rounded-2xl focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all ${
    dm('border-gray-200 bg-white text-gray-900', 'border-gray-600 bg-gray-700 text-gray-100')
  }`;
  
  const labelClass = `block text-sm font-semibold mb-3 ${dm('text-gray-700', 'text-gray-300')}`;
  
  const cardClass = `rounded-3xl shadow-xl border p-8 mb-8 transition-colors duration-300 ${
    dm('bg-white border-gray-100', 'bg-gray-800 border-gray-700')
  }`;
  
  const headingClass = `text-2xl font-semibold mb-6 flex items-center ${dm('text-gray-800', 'text-gray-100')}`;
  
  const itemInputClass = `w-full p-3 border-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all ${
    dm('border-gray-200 bg-white text-gray-900 hover:border-gray-300', 'border-gray-600 bg-gray-800 text-gray-100 hover:border-gray-500')
  }`;

  // Helper function to load image as data URL
  const loadImageAsDataUrl = (imageSrc) => {
    return new Promise((resolve, reject) => {
      try {
        const img = new window.Image();
        
        const timeout = setTimeout(() => {
          console.error('Image load timeout for:', imageSrc);
          reject(new Error('Image load timeout'));
        }, 5000); // 5 second timeout
        
        img.onload = function() {
          clearTimeout(timeout);
          console.log('Image loaded successfully');
          try {
            // Downscale huge logos before embedding. The source PNGs can be
            // up to 6000x6000, but the document shows the logo in a ~64px
            // box. Embedding the full-size image as a base64 data URL makes
            // Chrome's "Save as PDF" spend a very long time re-encoding it.
            // 320px is ~5x the display size — plenty for crisp 300dpi print.
            const MAX_DIMENSION = 320;
            const scale = Math.min(1, MAX_DIMENSION / Math.max(img.width, img.height));
            const canvas = document.createElement('canvas');
            canvas.width = Math.max(1, Math.round(img.width * scale));
            canvas.height = Math.max(1, Math.round(img.height * scale));
            const ctx = canvas.getContext('2d');
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            const dataUrl = canvas.toDataURL('image/png');
            
            // Clean up canvas immediately
            canvas.remove();
            console.log('Canvas converted to data URL');
            resolve(dataUrl);
          } catch (err) {
            console.error('Canvas error:', err);
            reject(err);
          }
        };
        
        img.onerror = function(e) {
          clearTimeout(timeout);
          console.error('Image failed to load:', imageSrc, e);
          reject(new Error('Failed to load image'));
        };
        
        console.log('Starting to load image:', imageSrc);
        img.src = imageSrc;
      } catch (err) {
        console.error('Error in loadImageAsDataUrl:', err);
        reject(err);
      }
    });
  };

  // PDF export: builds a fully self-contained HTML document and prints it
  // from a hidden same-origin iframe (no popup window, no blob: URL).
  const generateProfessionalPDF = async () => {
    try {
      console.log('Starting PDF generation...');
      const company = companies[currentOffer.company];
      const total = getSubtotal();
      
      // Simple price formatter for PDF - doesn't rely on external function
      const formatPriceForPDF = (price) => {
        const num = parseFloat(price || 0);
        if (isNaN(num)) return '0.00';
        return num.toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        });
      };

      // Attribute-safe HTML escaper. Every user-controlled value interpolated
      // into the print document MUST pass through this: offer fields round-trip
      // through the database and are rendered in a same-origin document, so an
      // unescaped value is stored XSS. Escapes quotes too so values are safe in
      // both text and attribute contexts.
      const escapeHtmlForPdf = (str) => String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
      // Render a Terms & Conditions value that may be a string (legacy) or
      // an array of strings (multiple entries). Empty entries are skipped so
      // we never render a blank row in the PDF.
      const renderTermsValueHtml = (value) => {
        const list = Array.isArray(value)
          ? value
          : value == null ? [] : [value];
        const cleaned = list
          .map((v) => (v == null ? '' : String(v).trim()))
          .filter((v) => v.length > 0);
        if (!cleaned.length) return '';
        if (cleaned.length === 1) return escapeHtmlForPdf(cleaned[0]);
        return cleaned
          .map((v) => `<div>${escapeHtmlForPdf(v)}</div>`)
          .join('');
      };
      
      // Function to normalize special characters for filenames
      const normalizeFilename = (text) => {
        const charMap = {
          'ü': 'u', 'ğ': 'g', 'ı': 'i', 'ş': 's', 'ç': 'c', 'ö': 'o',
          'Ü': 'U', 'Ğ': 'G', 'İ': 'I', 'Ş': 'S', 'Ç': 'C', 'Ö': 'O',
          'ä': 'a', 'Ä': 'A', 'ß': 'ss', 'é': 'e', 'è': 'e', 'ê': 'e',
          'É': 'E', 'È': 'E', 'Ê': 'E', 'à': 'a', 'À': 'A', 'ñ': 'n', 'Ñ': 'N'
        };
        return text.replace(/[üğışçöÜĞİŞÇÖäÄßéèêÉÈÊàÀñÑ]/g, char => charMap[char] || char);
      };
      
      // Generate filename
      const companyShort = currentOffer.company === 'fin-marine' ? 'FIN-MARINE' : 
                          currentOffer.company === 'fin-marine-fzco' ? 'FIN-MARINE-FZCO' : 'ATLANTIS';
      
      // Extract and clean customer name
      const addressIndicators = ['unit', 'street', 'road', 'avenue', 'building', 'floor', 'suite', 'office'];
      const customerWords = (currentOffer.customer.name || 'Customer')
        .split(/[\s\n,]+/) // Split by spaces, newlines, or commas
        .filter(word => word.trim()) // Remove empty strings
        .slice(0, 5) // Take first 5 words max
        .filter((word, index, arr) => {
          // Stop at address indicators
          const isAddress = addressIndicators.some(indicator => 
            word.toLowerCase().includes(indicator)
          );
          return !isAddress && arr.slice(0, index).every(w => 
            !addressIndicators.some(ind => w.toLowerCase().includes(ind))
          );
        })
        .slice(0, 4); // Ensure max 4 words after filtering
      
      const customerNormalized = normalizeFilename(customerWords.join('_'));
      const customerClean = customerNormalized.replace(/[^a-zA-Z0-9_]/g, '').replace(/_+/g, '_').replace(/^_|_$/g, '');
      
      const forClean = normalizeFilename(currentOffer.description || 'Equipment').replace(/[^a-zA-Z0-9]/g, '-');
      const offerTypeClean = currentOffer.offerType.replace(/\s+/g, '-');
      const filename = `${companyShort}-For-${forClean}-${customerClean}-${currentOffer.offerNumber}-${offerTypeClean}`;

      // Calculate validity date
      const offerDate = new Date(currentOffer.date);
      const validUntil = new Date(offerDate);
      validUntil.setDate(validUntil.getDate() + 14);
      const validUntilFormatted = validUntil.toLocaleDateString('en-GB');

      // Select logo based on company
      let logoSrc;
      if (currentOffer.company === 'fin-marine') {
        logoSrc = finMarineLogo;
      } else if (currentOffer.company === 'fin-marine-fzco') {
        logoSrc = finMarineIntlLogo;
      } else {
        logoSrc = atlantisLogo;
      }

      // Load logo as data URL with fallback
      console.log('Loading logo image...');
      let logoDataUrl = '';
      try {
        logoDataUrl = await loadImageAsDataUrl(logoSrc);
      } catch (error) {
        console.warn('Failed to load logo, continuing without it:', error);
        // Continue without logo rather than failing completely
        logoDataUrl = '';
      }
      
      const htmlContent = `
<!DOCTYPE html>
<html>
<head>
  <meta charset='UTF-8'>
  <title>${escapeHtmlForPdf(filename)}</title>
  <!-- No external stylesheets on purpose: the print document must be fully
       self-contained. Fetching fonts from a CDN made Chrome's print/save
       pipeline stall waiting on the network (slow "Save as PDF", and
       previously blank previews — Chromium issue 361375802). -->
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    /* Page 1 bleeds to the edges (dark header band); continuation pages get
       real top/bottom margins so content never starts at the paper edge. */
    @page {
      size: A4;
      margin: 6mm 0 5mm;
    }
    @page :first {
      margin: 0;
    }
    body {
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
      font-size: 11px;
      color: #1f2937;
      background: #fff;
      margin: 0;
      padding: 0;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    /* Layout tokens — keep horizontal rhythm consistent across the doc.
       --pad is the single source of truth for left/right page padding. */
    .page-container {
      width: 210mm;
      margin: 0 auto;
      background: #fff;
      position: relative;
      --pad: 28px;
    }
    .main-content {
      position: relative;
      padding-bottom: 26px; /* Space for footer (footer is 22px tall) */
    }

    /* Document Title */
    .document-title {
      text-align: center;
      font-size: 22px;
      font-weight: 700;
      color: #2c3e5a;
      letter-spacing: 0.6px;
      margin: 14px var(--pad) 12px;
      padding-bottom: 10px;
      border-bottom: 2px solid #2c3e5a;
    }

    /* Header */
    .header {
      background: #2c3e5a;
      color: white;
      padding: 18px var(--pad);
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 18px;
    }
    .header-left {
      display: flex;
      align-items: center;
      gap: 16px;
      flex: 1;
      min-width: 0;
    }
    .logo-container {
      width: 64px;
      height: 64px;
      background: white;
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 7px;
      flex-shrink: 0;
    }
    .logo-container img {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
    .company-info {
      flex: 1;
      min-width: 0;
    }
    .company-info h1 {
      font-size: 17px;
      font-weight: 700;
      margin-bottom: 3px;
      letter-spacing: 0.2px;
      line-height: 1.2;
    }
    .company-info .subtitle {
      font-size: 12px;
      font-weight: 500;
      opacity: 0.92;
      margin-bottom: 4px;
    }
    .company-info .address {
      font-size: 9.5px;
      opacity: 0.78;
      line-height: 1.5;
    }
    .header-right {
      text-align: right;
      font-size: 10px;
      opacity: 0.88;
      flex-shrink: 0;
      align-self: center;
    }

    /* Offer Info Bar */
    .offer-info {
      background: #f8fafc;
      border: 1px solid #e2e8f0;
      border-radius: 6px;
      padding: 10px 18px;
      display: flex;
      justify-content: space-between;
      margin: 12px var(--pad) 0;
      gap: 24px;
      break-inside: avoid;
      page-break-inside: avoid;
    }
    .offer-info-item {
      flex: 1;
      min-width: 0;
    }
    .offer-info-label {
      font-size: 9px;
      text-transform: uppercase;
      letter-spacing: 0.7px;
      color: #64748b;
      font-weight: 600;
      margin-bottom: 5px;
    }
    .offer-info-value {
      font-size: 16px;
      font-weight: 700;
      color: #2c3e5a;
      letter-spacing: 0.2px;
    }
    .offer-info-value.date {
      font-size: 14px;
      font-weight: 600;
    }

    /* Section Headers — never leave a heading orphaned at the bottom of a
       page with its content pushed to the next one. */
    .section-header {
      margin: 13px var(--pad) 7px;
      break-after: avoid;
      page-break-after: avoid;
      break-inside: avoid;
      page-break-inside: avoid;
    }
    .section-title {
      font-size: 13px;
      font-weight: 600;
      color: #2c3e5a;
      letter-spacing: 0.2px;
      margin-bottom: 5px;
    }
    .section-underline {
      width: 56px;
      height: 2px;
      background: #4CAF50;
      border-radius: 1px;
    }

    /* Customer Info Grid */
    .info-grid {
      margin: 0 var(--pad);
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 10px;
    }
    /* Auto-balance: when total cell count is odd, the trailing cell spans
       both columns instead of leaving an empty slot on the right. */
    .info-grid > .info-box:last-child:nth-child(odd) {
      grid-column: 1 / -1;
    }
    .info-box {
      background: #fff;
      border: 1px solid #e2e8f0;
      border-left: 3px solid #2c3e5a;
      border-radius: 4px;
      padding: 8px 12px;
      display: flex;
      flex-direction: column;
      gap: 3px;
      min-height: 42px;
      justify-content: center;
      break-inside: avoid;
      page-break-inside: avoid;
    }
    .info-label {
      font-size: 8.5px;
      text-transform: uppercase;
      letter-spacing: 0.7px;
      color: #64748b;
      font-weight: 600;
    }
    .info-value {
      font-size: 11.5px;
      color: #1f2937;
      font-weight: 500;
      line-height: 1.4;
      word-break: break-word;
    }

    /* Default tables (used by package summary, weight summary, discount table) */
    .table-container {
      margin: 0 var(--pad);
    }
    table {
      width: 100%;
      border-collapse: collapse;
      font-size: 8.5px;
      table-layout: fixed;
    }
    /* Items table — auto layout + tasteful spacing */
    table.items-table {
      table-layout: auto;
      border: 1px solid #e2e8f0;
      border-radius: 6px;
      overflow: hidden;
    }
    table.items-table th,
    table.items-table td {
      white-space: normal;
      word-wrap: break-word;
      overflow: visible;
    }
    table.items-table tr {
      page-break-inside: avoid;
      break-inside: avoid;
    }
    table.items-table thead th {
      background: #2c3e5a;
      color: white;
      padding: 8px 4px;
      text-align: center;
      font-size: 8px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      border: none;
      white-space: nowrap;
    }
    table.items-table tbody td {
      border-top: 1px solid #e2e8f0;
      border-left: none;
      border-right: none;
      border-bottom: none;
      padding: 7px 5px;
      background: #fff;
      font-size: 9px;
      line-height: 1.4;
      vertical-align: middle;
    }
    table.items-table tbody tr:first-child td {
      border-top: none;
    }
    table.items-table tbody tr:nth-child(even) td {
      background: #f8fafc;
    }
    /* Generic fallback for any non-items tables (legacy/embedded) */
    th {
      background: #2c3e5a;
      color: white;
      padding: 5px 3px;
      text-align: left;
      font-size: 8px;
      font-weight: 500;
      white-space: nowrap;
      overflow: hidden;
    }
    td {
      border: 1px solid #e2e8f0;
      padding: 5px 3px;
      background: #fff;
      font-size: 8.5px;
      vertical-align: top;
      word-wrap: break-word;
    }
    .text-center { text-align: center; }
    .text-right { text-align: right; }

    /* Total Section — wraps total/discount/final-total so they all align */
    .total-section {
      margin: 10px var(--pad) 0;
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      gap: 6px;
      page-break-inside: avoid;
      break-inside: avoid;
    }
    .total-box {
      background: #2c3e5a;
      color: white;
      padding: 9px 20px;
      text-align: right;
      border-radius: 6px;
      min-width: 280px;
    }
    .total-label {
      font-size: 10px;
      font-weight: 500;
      text-transform: uppercase;
      letter-spacing: 0.6px;
      opacity: 0.85;
      margin-bottom: 3px;
    }
    .total-value {
      font-size: 19px;
      font-weight: 700;
      letter-spacing: 0.3px;
    }
    .discount-row {
      min-width: 280px;
      padding: 6px 22px;
      font-size: 11px;
      font-weight: 600;
      color: #d73027;
      display: flex;
      justify-content: space-between;
      gap: 16px;
      border-bottom: 2px solid #2c3e5a;
    }

    /* Terms & Bank Section */
    .terms-grid {
      margin: 0 var(--pad);
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 12px;
      page-break-inside: avoid;
      break-inside: avoid;
    }
    .terms-box, .bank-box {
      background: #f8fafc;
      border: 1px solid #e2e8f0;
      border-radius: 6px;
      padding: 9px 14px;
      page-break-inside: avoid;
      break-inside: avoid;
    }
    .terms-item {
      margin-bottom: 6px;
    }
    .terms-item:last-child { margin-bottom: 0; }
    .terms-label {
      font-size: 8px;
      text-transform: uppercase;
      letter-spacing: 0.7px;
      color: #64748b;
      font-weight: 600;
      margin-bottom: 2px;
    }
    .terms-value {
      font-size: 10px;
      color: #1f2937;
      line-height: 1.35;
    }
    .bank-title {
      font-size: 9.5px;
      font-weight: 700;
      color: #2c3e5a;
      text-transform: uppercase;
      letter-spacing: 0.7px;
      padding-bottom: 5px;
      margin-bottom: 7px;
      border-bottom: 1px solid #e2e8f0;
    }
    .bank-item {
      display: flex;
      font-size: 9.5px;
      margin-bottom: 3px;
      align-items: baseline;
      line-height: 1.3;
    }
    .bank-item:last-child { margin-bottom: 0; }
    .bank-label {
      color: #64748b;
      min-width: 60px;
      font-weight: 500;
    }
    .bank-value {
      color: #1f2937;
      flex: 1;
      font-weight: 500;
      word-break: break-all;
    }

    /* Warning Box */
    .warning-box {
      background: #fffbeb;
      border: 1px solid #fde68a;
      border-left: 3px solid #f59e0b;
      padding: 8px 14px;
      margin: 10px var(--pad) 0;
      color: #78350f;
      font-size: 10px;
      border-radius: 4px;
      line-height: 1.4;
      page-break-inside: avoid;
      break-inside: avoid;
    }
    .warning-box::before {
      content: '⚠ ';
      font-size: 12px;
      margin-right: 4px;
    }

    /* Important Notice — kept intentionally compact so it doesn't push
       the footer onto a second page */
    .important-notice {
      background: #fef2f2;
      border: 1px solid #fecaca;
      border-left: 3px solid #dc2626;
      padding: 4px 10px 5px;
      margin: 6px var(--pad) 4px;
      border-radius: 4px;
      page-break-inside: avoid;
      break-inside: avoid;
    }
    .notice-title {
      font-size: 7.5px;
      font-weight: 700;
      color: #991b1b;
      margin-bottom: 2px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
    .important-notice ul {
      margin: 0;
      padding-left: 13px;
      list-style-type: disc;
    }
    .important-notice li {
      font-size: 7px;
      color: #7f1d1d;
      margin-bottom: 1px;
      line-height: 1.3;
    }
    .important-notice li:last-child {
      margin-bottom: 0;
    }

    /* Footer — single slim line */
    .footer {
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      padding: 4px var(--pad);
      border-top: 1px solid #e2e8f0;
      height: 22px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      background: white;
    }
    .footer-left {
      flex: 1;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .footer-company {
      display: inline;
      font-size: 7.5px;
      color: #64748b;
      font-weight: 500;
    }
    .footer-website {
      display: inline;
      font-size: 7.5px;
      color: #94a3b8;
      margin-left: 6px;
    }
    .footer-right {
      text-align: right;
      flex-shrink: 0;
    }
    .page-number {
      font-size: 7.5px;
      color: #94a3b8;
    }

    @media print {
      body {
        background: #fff !important;
        margin: 0 !important;
        padding: 0 !important;
      }
      .page-container {
        box-shadow: none !important;
        margin: 0 !important;
        width: 210mm !important;
        height: auto !important;
      }
      .header,
      .total-box,
      .items-table thead th {
        print-color-adjust: exact;
        -webkit-print-color-adjust: exact;
      }
    }
  </style>
</head>
<body>
  <div class='page-container'>
    <div class='main-content'>
  <!-- Document Title -->
  <div class='document-title'>${escapeHtmlForPdf(currentOffer.offerType)}</div>
  
    <!-- Header -->
    <div class='header'>
      <div class='header-left'>
        <div class='logo-container'>
          ${logoDataUrl ? `<img src='${logoDataUrl}' alt='Company Logo' />` : '<div style="width: 100px; height: 40px;"></div>'}
        </div>
        <div class='company-info'>
          <h1>${company.name.includes('INTERNATIONAL') ? 'FIN MARINE & ENERGY SOLUTIONS' : company.name}</h1>
          ${company.name.includes('INTERNATIONAL') ? "<div class='subtitle'>INTERNATIONAL FZCO</div>" : ''}

          <div class='address'>${company.address}</div>
    </div>
    </div>
      <div class='header-right'>
        ${company.email}
    </div>
    </div>
    
    <!-- Offer Info -->
    <div class='offer-info'>
      <div class='offer-info-item'>
        <div class='offer-info-label'>${escapeHtmlForPdf(currentOffer.offerType)} NUMBER</div>
        <div class='offer-info-value'>//${escapeHtmlForPdf(currentOffer.offerNumber || '')}</div>
  </div>
      <div class='offer-info-item'>
        <div class='offer-info-label'>DATE</div>
        <div class='offer-info-value date'>${currentOffer.date ? new Date(currentOffer.date).toLocaleDateString('en-GB') : ''}</div>
      </div>
      ${currentOffer.offerType === 'OFFER' ? `
      <div class='offer-info-item'>
        <div class='offer-info-label'>VALID UNTIL</div>
        <div class='offer-info-value date'>${validUntilFormatted}</div>
      </div>
      ` : ''}
    </div>
    
    <!-- Customer/Supplier Information -->
    <div class='section-header'>
      <h2 class='section-title'>${(currentOffer.offerType === 'PURCHASE ORDER' || currentOffer.offerType === 'INQUIRY') ? 'Supplier Information' : 'Customer Information'}</h2>
      <div class='section-underline'></div>
    </div>
    
    <div class='info-grid'>
      <div class='info-box'>
        <span class='info-label'>${(currentOffer.offerType === 'PURCHASE ORDER' || currentOffer.offerType === 'INQUIRY') ? 'SUPPLIER:' : 'CUSTOMER:'}</span>
        <span class='info-value'>${escapeHtmlForPdf(currentOffer.customer.name || '')}</span>
      </div>
      <div class='info-box'>
        <span class='info-label'>PROJECT:</span>
        <span class='info-value'>${escapeHtmlForPdf(currentOffer.customer.project || '')}</span>
      </div>
      <div class='info-box'>
        <span class='info-label'>FOR:</span>
        <span class='info-value'>${escapeHtmlForPdf(currentOffer.description || '')}</span>
      </div>
      ${currentOffer.offerType !== 'PACKING LIST' && currentOffer.offerType !== 'PURCHASE ORDER' && currentOffer.engineSerialNumber && currentOffer.engineSerialNumber.trim() !== '' ? `
      <div class='info-box'>
        <span class='info-label'>ENGINE SERIAL NO:</span>
        <span class='info-value'>${escapeHtmlForPdf(currentOffer.engineSerialNumber)}</span>
      </div>
      ` : ''}
    </div>
    
    <!-- Items Specification -->
    <div class='section-header'>
      <h2 class='section-title'>Items Specification</h2>
      <div class='section-underline'></div>
    </div>
    
    <div class='table-container'>
      <table class='items-table'>
      <thead>
        <tr>
            <th style='min-width: 22px;'>ITEM</th>
            <th style='min-width: 140px;'>DESCRIPTION</th>
            <th style='min-width: 45px;'>PART NO</th>
            ${shouldShowCOOColumn() ? `<th style='min-width: 50px;'>COO</th>` : ''}
            ${shouldShowMakeColumn() ? `<th style='min-width: 42px;'>MAKE</th>` : ''}
            <th style='min-width: 55px;'>CONDITION</th>
            <th style='min-width: 22px;'>QTY</th>
            ${currentOffer.offerType === 'PACKING LIST' ? `<th style='min-width: 58px;'>WEIGHT (KG)</th>` : ''}
            ${currentOffer.offerType === 'PACKING LIST' ? `<th style='min-width: 50px;'>PACKAGE</th>` : ''}
            ${currentOffer.offerType === 'PURCHASE ORDER' ? `<th style='min-width: 50px;'>CURRENCY</th>` : ''}
            ${currentOffer.offerType !== 'PACKING LIST' && currentOffer.offerType !== 'INQUIRY' ? `<th style='min-width: 65px;'>UNIT PRICE</th>` : ''}
            ${currentOffer.offerType !== 'PACKING LIST' && currentOffer.offerType !== 'INQUIRY' ? `<th style='min-width: 70px;'>TOTAL PRICE</th>` : ''}
            <th style='min-width: 48px;'>REMARKS</th>
            ${currentOffer.offerType !== 'PACKING LIST' ? `<th style='min-width: 60px;'>AVAILABILITY</th>` : ''}
        </tr>
      </thead>
      <tbody>
          ${currentOffer.items.map((item, i) => `
            <tr>
              <td class='text-center'>${i + 1}</td>
              <td>${escapeHtmlForPdf(item.description || '')}</td>
              <td>${escapeHtmlForPdf(item.partNo || '')}</td>
              ${shouldShowCOOColumn() ? `<td class='text-center'>${escapeHtmlForPdf(getCOODisplay(item))}</td>` : ''}
              ${shouldShowMakeColumn() ? `<td class='text-center'>${escapeHtmlForPdf(getMakeDisplay(item))}</td>` : ''}
              <td class='text-center'><strong>${escapeHtmlForPdf(item.condition || 'NEW')}</strong></td>
              <td class='text-center'>${escapeHtmlForPdf(item.qty || '')}</td>
              ${currentOffer.offerType === 'PACKING LIST' ? `<td class='text-center'>${escapeHtmlForPdf(item.weight || 0)} KG</td>` : ''}
              ${currentOffer.offerType === 'PACKING LIST' ? `<td class='text-center'>${escapeHtmlForPdf(item.packageNumber || 'Box 1')}</td>` : ''}
              ${currentOffer.offerType === 'PURCHASE ORDER' ? `<td class='text-center'>${escapeHtmlForPdf(item.currency || 'USD')}</td>` : ''}
              ${currentOffer.offerType !== 'PACKING LIST' && currentOffer.offerType !== 'INQUIRY' ? `<td class='text-right'>${(currentOffer.offerType === 'PURCHASE ORDER' ? item.unitPrice : getAdjustedUnitPrice(item)) ? escapeHtmlForPdf(currentOffer.offerType === 'PURCHASE ORDER' ? (item.currency || 'USD') : currentOffer.currency) + ' ' + formatPriceForPDF(currentOffer.offerType === 'PURCHASE ORDER' ? item.unitPrice : getAdjustedUnitPrice(item)) : ''}</td>` : ''}
              ${currentOffer.offerType !== 'PACKING LIST' && currentOffer.offerType !== 'INQUIRY' ? `<td class='text-right'>${(currentOffer.offerType === 'PURCHASE ORDER' ? item.totalPrice : getAdjustedTotalPrice(item)) ? escapeHtmlForPdf(currentOffer.offerType === 'PURCHASE ORDER' ? (item.currency || 'USD') : currentOffer.currency) + ' ' + formatPriceForPDF(currentOffer.offerType === 'PURCHASE ORDER' ? item.totalPrice : getAdjustedTotalPrice(item)) : ''}</td>` : ''}
              <td>${escapeHtmlForPdf(item.remarks || '')}</td>
              ${currentOffer.offerType !== 'PACKING LIST' ? `<td>${currentOffer.offerType === 'INQUIRY' ? '' : escapeHtmlForPdf(item.availability || 'Ex works - Subject unsold')}</td>` : ''}
          </tr>
        `).join('')}
      </tbody>
    </table>
  </div>
    
    ${currentOffer.offerType !== 'PACKING LIST' && currentOffer.offerType !== 'PURCHASE ORDER' && currentOffer.offerType !== 'INQUIRY' ? `
    <!-- Totals (total + optional discount + final total) -->
    <div class='total-section'>
      <div class='total-box'>
        <div class='total-label'>TOTAL (${escapeHtmlForPdf(currentOffer.currency)})</div>
        <div class='total-value'>${escapeHtmlForPdf(currentOffer.currency)} ${formatPriceForPDF(total)}</div>
      </div>
      ${currentOffer.discountEnabled && currentOffer.discountAmount > 0 ? `
      <div class='discount-row'>
        <span>${currentOffer.discountLabel === 'paid' ? 'Already Paid' : 'Discount'} (${currentOffer.discountType === 'percentage' ? escapeHtmlForPdf(currentOffer.discountValue) + '%' : escapeHtmlForPdf(currentOffer.currency) + ' ' + formatPriceForPDF(currentOffer.discountValue)})</span>
        <span>-${escapeHtmlForPdf(currentOffer.currency)} ${formatPriceForPDF(currentOffer.discountAmount)}</span>
      </div>
      <div class='total-box'>
        <div class='total-label'>FINAL TOTAL</div>
        <div class='total-value'>${escapeHtmlForPdf(currentOffer.currency)} ${formatPriceForPDF(total - currentOffer.discountAmount)}</div>
      </div>
      ` : ''}
    </div>
    ` : ''}

    ${currentOffer.offerType === 'PURCHASE ORDER' ? `
    <!-- Purchase Order totals — one box per currency since each item can
         carry its own currency. -->
    <div class='total-section'>
      ${getPurchaseOrderTotalsByCurrency().map(({ currency, amount }) => `
      <div class='total-box'>
        <div class='total-label'>TOTAL (${escapeHtmlForPdf(currency)})</div>
        <div class='total-value'>${escapeHtmlForPdf(currency)} ${formatPriceForPDF(amount)}</div>
      </div>
      `).join('')}
    </div>
    ` : ''}
    
    ${currentOffer.offerType === 'PACKING LIST' ? `
    <!-- Package Summary -->
    <div class='section-header'>
      <h2 class='section-title'>Package Summary</h2>
      <div class='section-underline'></div>
    </div>
    
    <div style='margin: 20px 30px;'>
      <table style='width: 100%; border-collapse: collapse;'>
        <thead>
          <tr style='background-color: #f5f5f5;'>
            <th style='border: 1px solid #ddd; padding: 8px; text-align: left;'>Package</th>
            <th style='border: 1px solid #ddd; padding: 8px; text-align: left;'>Type</th>
            <th style='border: 1px solid #ddd; padding: 8px; text-align: center;'>Dimensions (cm)</th>
            <th style='border: 1px solid #ddd; padding: 8px; text-align: center;'>Box Weight (${escapeHtmlForPdf(weightUnit)})</th>
            <th style='border: 1px solid #ddd; padding: 8px; text-align: center;'>Items Weight (${escapeHtmlForPdf(weightUnit)})</th>
            <th style='border: 1px solid #ddd; padding: 8px; text-align: center;'>Total Weight (${escapeHtmlForPdf(weightUnit)})</th>
          </tr>
        </thead>
        <tbody>
          ${packages.map(pkg => {
            const packageWeight = currentOffer.items
              .filter(item => item.packageNumber === pkg.number)
              .reduce((sum, item) => sum + (parseFloat(item.weight || 0) * parseFloat(item.qty || 0)), 0);
            const itemCount = currentOffer.items.filter(item => item.packageNumber === pkg.number).length;
            
            return `
            <tr>
              <td style='border: 1px solid #ddd; padding: 8px;'>${escapeHtmlForPdf(pkg.number)}</td>
              <td style='border: 1px solid #ddd; padding: 8px;'>${escapeHtmlForPdf(pkg.type)}</td>
              <td style='border: 1px solid #ddd; padding: 8px; text-align: center;'>
                ${escapeHtmlForPdf(pkg.length || pkg.dimensions?.length || 0)} × ${escapeHtmlForPdf(pkg.width || pkg.dimensions?.width || 0)} × ${escapeHtmlForPdf(pkg.height || pkg.dimensions?.height || 0)}
              </td>
              <td style='border: 1px solid #ddd; padding: 8px; text-align: center;'>${(pkg.boxWeight || 0).toFixed(2)}</td>
              <td style='border: 1px solid #ddd; padding: 8px; text-align: center;'>${packageWeight.toFixed(2)}</td>
              <td style='border: 1px solid #ddd; padding: 8px; text-align: center;'>${(packageWeight + (pkg.boxWeight || 0)).toFixed(2)}</td>
            </tr>
            `;
          }).join('')}
        </tbody>
      </table>
    </div>
    
    <!-- Weight Summary -->
    <div style='margin: 20px 30px; padding: 15px; background-color: #f0f9ff; border: 1px solid #bfdbfe; border-radius: 8px;'>
      <h3 style='margin: 0 0 10px 0; color: #1e40af; font-size: 14px;'>Weight Summary</h3>
      <table style='width: 100%; font-size: 12px;'>
        <tr>
          <td style='padding: 4px 0;'>Number of packages:</td>
          <td style='text-align: right; font-weight: bold;'>${packages.length}</td>
        </tr>
        <tr>
          <td style='padding: 4px 0;'>Total items weight:</td>
          <td style='text-align: right; font-weight: bold;'>${currentOffer.items.reduce((sum, item) => sum + (parseFloat(item.weight || 0) * parseFloat(item.qty || 0)), 0).toFixed(2)} ${escapeHtmlForPdf(weightUnit)}</td>
        </tr>
        <tr>
          <td style='padding: 4px 0;'>Total packaging weight:</td>
          <td style='text-align: right; font-weight: bold;'>${packages.reduce((sum, pkg) => sum + (pkg.boxWeight || 0), 0).toFixed(2)} ${escapeHtmlForPdf(weightUnit)}</td>
        </tr>
        <tr style='border-top: 2px solid #1e40af; margin-top: 8px;'>
          <td style='padding: 8px 0 4px 0; font-weight: bold; color: #1e40af;'>Total gross weight:</td>
          <td style='text-align: right; font-weight: bold; color: #1e40af; font-size: 14px;'>
            ${(currentOffer.items.reduce((sum, item) => sum + (parseFloat(item.weight || 0) * parseFloat(item.qty || 0)), 0) + packages.reduce((sum, pkg) => sum + (parseFloat(pkg.boxWeight || 0)), 0)).toFixed(2)} ${escapeHtmlForPdf(weightUnit)}
          </td>
        </tr>
      </table>
    </div>
    
    <!-- Addresses -->
    <div class='section-header'>
      <h2 class='section-title'>Shipping Information</h2>
      <div class='section-underline'></div>
    </div>
    
    <div class='terms-grid'>
      <div class='terms-item'>
        <div class='terms-label'>PICKUP ADDRESS:</div>
        <div class='terms-value'>${escapeHtmlForPdf(pickupAddress || 'Not specified')}</div>
      </div>
      ${showConsignee ? `
      <div class='terms-item'>
        <div class='terms-label'>CONSIGNEE ADDRESS:</div>
        <div class='terms-value'>${escapeHtmlForPdf(consigneeAddress || 'Not specified')}</div>
      </div>
      ` : ''}
      <div class='terms-item'>
        <div class='terms-label'>DELIVERY ADDRESS:</div>
        <div class='terms-value'>${escapeHtmlForPdf(deliveryAddress || 'Not specified')}</div>
      </div>
    </div>
    ` : ''}
    
    ${showOfferAddresses ? `
    <!-- Shipping Addresses -->
    <div class='section-header'>
      <h2 class='section-title'>Shipping Addresses</h2>
      <div class='section-underline'></div>
    </div>
    
    <div class='terms-grid'>
      <div class='terms-item'>
        <div class='terms-label'>PICKUP ADDRESS:</div>
        <div class='terms-value'>${escapeHtmlForPdf(offerPickupAddress || 'Not specified')}</div>
      </div>
      <div class='terms-item'>
        <div class='terms-label'>SHIPPING ADDRESS:</div>
        <div class='terms-value'>${escapeHtmlForPdf(offerShippingAddress || 'Not specified')}</div>
      </div>
    </div>
    ` : ''}
    
    ${showOfferComments ? `
    <!-- Comments -->
    <div class='section-header'>
      <h2 class='section-title'>Comments</h2>
      <div class='section-underline'></div>
    </div>
    
    <div class='terms-grid'>
      <div class='terms-item' style='grid-column: span 2;'>
        <div class='terms-value'>${escapeHtmlForPdf(offerComments || '')}</div>
      </div>
    </div>
    ` : ''}
    
    <!-- Terms & Conditions -->
    <div class='section-header'>
      <h2 class='section-title'>Terms & Conditions</h2>
      <div class='section-underline'></div>
      </div>
    
    <div class='terms-grid'>
      <div class='terms-box'>
        ${currentOffer.offerType === 'INQUIRY' ? `
        <div class='terms-item'>
          <div class='terms-value'>Please include your terms and conditions, including delivery time, best price, and payment terms.</div>
        </div>
        ` : `
        <div class='terms-item'>
          <div class='terms-label'>DELIVERY TERMS:</div>
          <div class='terms-value'>${renderTermsValueHtml(currentOffer.terms.deliveryTerms)}</div>
      </div>
        <div class='terms-item'>
          <div class='terms-label'>DELIVERY TIME:</div>
          <div class='terms-value'>${renderTermsValueHtml(currentOffer.terms.deliveryTime)}</div>
    </div>
        <div class='terms-item'>
          <div class='terms-label'>PAYMENT TERMS:</div>
          <div class='terms-value'>${renderTermsValueHtml(currentOffer.terms.paymentTerms)}</div>
  </div>
        `}
    </div>
      
      ${currentOffer.offerType !== 'PACKING LIST' && currentOffer.offerType !== 'PURCHASE ORDER' && currentOffer.offerType !== 'INQUIRY' ? `
      <div class='bank-box'>
        <div class='bank-title'>BANK DETAILS</div>
        <div class='bank-item'>
          <span class='bank-label'>Holder:</span>
          <span class='bank-value'>${escapeHtmlForPdf(currentOffer.bankDetails.holder || '')}</span>
  </div>
        <div class='bank-item'>
          <span class='bank-label'>Bank:</span>
          <span class='bank-value'>${escapeHtmlForPdf(currentOffer.bankDetails.bank || '')}</span>
        </div>
        ${currentOffer.bankDetails.branch ? `
        <div class='bank-item'>
          <span class='bank-label'>Branch:</span>
          <span class='bank-value'>${escapeHtmlForPdf(currentOffer.bankDetails.branch)}</span>
        </div>` : ''}
        ${currentOffer.bankDetails.accountNo ? `
        <div class='bank-item'>
          <span class='bank-label'>Account:</span>
          <span class='bank-value'>${escapeHtmlForPdf(currentOffer.bankDetails.accountNo)}</span>
        </div>` : ''}
        <div class='bank-item'>
          <span class='bank-label'>IBAN:</span>
          <span class='bank-value'>${escapeHtmlForPdf(currentOffer.bankDetails.iban || '')}</span>
        </div>
        <div class='bank-item'>
          <span class='bank-label'>SWIFT:</span>
          <span class='bank-value'>${escapeHtmlForPdf(currentOffer.bankDetails.swiftCode || '')}</span>
        </div>
        ${currentOffer.bankDetails.routingCode ? `
        <div class='bank-item'>
          <span class='bank-label'>Routing:</span>
          <span class='bank-value'>${escapeHtmlForPdf(currentOffer.bankDetails.routingCode)}</span>
        </div>` : ''}
      </div>
      ` : ''}
    </div>
    
    <!-- Warning - Only show for offers -->
    ${currentOffer.offerType === 'OFFER' ? `
    <div class='warning-box'>
      This offer is valid for 2 weeks from the offer date.
    </div>
    ` : ''}
    
    ${currentOffer.offerType !== 'PACKING LIST' && currentOffer.offerType !== 'PURCHASE ORDER' && currentOffer.offerType !== 'INQUIRY' ? `
    <!-- Important Notice -->
    <div class='important-notice'>
      <div class='notice-title'>⚠️ IMPORTANT NOTICE</div>
      <ul>
        <li>Our company shall not be held liable for any payments made to third parties or to accounts at banks or branches other than those explicitly specified by our company.</li>
        <li>If you receive any communication regarding alternative bank account details, please contact us immediately by phone or WhatsApp to verify the authenticity of such instructions.</li>
      </ul>
    </div>
    ` : ''}
    
    </div> <!-- End main-content -->
    
    <!-- Footer -->
    <div class='footer'>
      <div class='footer-left'>
      <div class='footer-company'>${company.name}</div>
         <div class='footer-website'>www.${company.name.includes('FIN MARINE') ? 'fin-marine' : 'atlantismarinepower'}.com</div>
       </div>
      <div class='footer-right'>
      <div class='page-number'>Page 1</div>
      </div>
    </div>
  </div>
  <script>
    // Trigger print from INSIDE this document, after its own load event.
    // Calling print() from the opener on a timer causes a blank print
    // preview in newer Chrome when the fonts stylesheet is still loading
    // (Chromium issue 361375802): the print snapshot is taken before the
    // document has rendered. The load event waits for all resources.
    (function () {
      var printed = false;
      function triggerPrint() {
        if (printed) return;
        printed = true;
        setTimeout(function () {
          window.focus();
          window.print();
        }, 200);
      }
      if (document.readyState === 'complete') {
        triggerPrint();
      } else {
        window.addEventListener('load', triggerPrint);
        // Safety net in case load never fires. The document is fully
        // self-contained (inline styles, data-URL images), so load should
        // fire almost immediately.
        setTimeout(triggerPrint, 1000);
      }
    })();
  </script>
</body>
</html>
`;
      
      // Print via a hidden same-origin iframe loaded through srcdoc.
      // - No blob: URL anywhere in the print path (blob popups stalled the
      //   interactive "Save as PDF" step ~60s on some Chrome/macOS setups).
      // - No document.write either, which was unreliable in newer Chrome
      //   (the popup's initial about:blank navigation could commit AFTER the
      //   write and wipe the injected document). srcdoc is a normal document
      //   load with no such race.
      // The document auto-prints itself via its embedded script after its
      // own load event, exactly like the popup did.
      console.log('Opening print dialog via hidden iframe...');

      // Replace any iframe left over from a previous export.
      const previousFrame = document.getElementById('offer-print-frame');
      if (previousFrame) previousFrame.remove();

      const printFrame = document.createElement('iframe');
      printFrame.id = 'offer-print-frame';
      // Chrome will not print a display:none iframe; keep it rendered but
      // invisible and non-interactive instead.
      printFrame.style.cssText = 'position:fixed;left:0;top:0;width:1px;height:1px;opacity:0;pointer-events:none;border:0;';
      printFrame.setAttribute('aria-hidden', 'true');
      printFrame.srcdoc = htmlContent;

      // Chrome takes the "Save as PDF" suggested filename from the top-level
      // page title, not the iframe's <title>. Hold the offer filename in the
      // title until printing is done, then restore it.
      const previousTitle = document.title;
      document.title = filename;
      let printCleaned = false;
      const cleanupAfterPrint = () => {
        if (printCleaned) return;
        printCleaned = true;
        document.title = previousTitle;
        // The iframe itself is intentionally NOT removed here on a timer:
        // while the print dialog is open, Chrome re-renders from the frame
        // when the user clicks Save. It is removed on afterprint (dialog
        // closed) below, or replaced on the next export.
      };
      printFrame.addEventListener('load', () => {
        try {
          printFrame.contentWindow.addEventListener('afterprint', () => {
            // afterprint fires once the print dialog is closed (saved or
            // cancelled); give Chrome a moment before tearing down.
            setTimeout(() => {
              cleanupAfterPrint();
              printFrame.remove();
            }, 1000);
          });
        } catch (listenerError) {
          console.warn('Could not attach afterprint listener:', listenerError);
        }
      });
      // Generous fallback: restore the title even if afterprint never fires.
      // (The user may keep the dialog open for a long time, so this is long.)
      setTimeout(cleanupAfterPrint, 10 * 60 * 1000);

      document.body.appendChild(printFrame);

      console.log('PDF generation completed successfully');
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      
      // Show user-friendly error message
      if (error.message.includes('timeout')) {
        alert('The PDF generation timed out. Please try again.');
      } else {
        alert('There was an error generating the PDF. Please try again.');
      }
    } finally {
      // Clean up any remaining canvases
      const canvases = document.querySelectorAll('canvas');
      canvases.forEach(canvas => {
        if (canvas.parentNode) {
          canvas.remove();
        }
      });
    }
  };

  const downloadExcel = async () => {
    try {
      const company = companies[currentOffer.company];
      const offerType = normalizeOfferType(currentOffer.offerType);
      const priced = ['OFFER', 'PROFORMA INVOICE', 'INVOICE', 'COMMERCIAL INVOICE'].includes(offerType);
      const purchaseOrder = offerType === 'PURCHASE ORDER';
      const packing = offerType === 'PACKING LIST';
      const inquiry = offerType === 'INQUIRY';
      const cooShown = shouldShowCOOColumn();
      const makeShown = shouldShowMakeColumn();

      let logoBase64 = null;
      try {
        let logoUrl = finMarineLogo;
        if (currentOffer.company === 'fin-marine-fzco') logoUrl = finMarineIntlLogo;
        else if (currentOffer.company !== 'fin-marine') logoUrl = atlantisLogo;
        const logoDataUrl = await loadImageAsDataUrl(logoUrl);
        logoBase64 = String(logoDataUrl).split(',')[1];
      } catch (error) {
        console.warn('Failed to load logo, continuing without it:', error);
      }

      const items = (currentOffer.items || []).map((item) => ({
        description: item.description || '',
        partNo: item.partNo || '',
        condition: item.condition || 'NEW',
        coo: cooShown ? getCOODisplay(item) : '',
        make: makeShown ? getMakeDisplay(item) : '',
        qty: parseFloat(item.qty) || 0,
        unitPrice: purchaseOrder
          ? (parseFloat(item.unitPrice) || 0)
          : (priced ? getAdjustedUnitPrice(item) : null),
        lineTotal: purchaseOrder
          ? (parseFloat(item.totalPrice) || 0)
          : (priced ? getAdjustedTotalPrice(item) : null),
        currency: purchaseOrder ? (item.currency || 'USD') : currentOffer.currency,
        remarks: item.remarks || '',
        availability: (packing || inquiry) ? '' : (item.availability || 'Ex works - Subject unsold'),
        weight: parseFloat(item.weight) || 0,
        packageNumber: item.packageNumber || 'Box 1'
      }));

      const subtotal = getSubtotal();
      const discountActive = priced && currentOffer.discountEnabled && Number(currentOffer.discountAmount) > 0;
      const packageRows = packing ? packages.map((pkg) => {
        const itemsWeight = (currentOffer.items || [])
          .filter((item) => item.packageNumber === pkg.number)
          .reduce((sum, item) => sum + ((parseFloat(item.weight) || 0) * (parseFloat(item.qty) || 0)), 0);
        const boxWeight = parseFloat(pkg.boxWeight) || 0;
        const length = pkg.length || pkg.dimensions?.length || 0;
        const width = pkg.width || pkg.dimensions?.width || 0;
        const height = pkg.height || pkg.dimensions?.height || 0;
        return {
          number: pkg.number,
          type: pkg.type,
          dimensions: `${length} × ${width} × ${height}`,
          boxWeight,
          itemsWeight,
          totalWeight: itemsWeight + boxWeight
        };
      }) : [];
      const itemsWeightTotal = packing
        ? (currentOffer.items || []).reduce((sum, item) => sum + ((parseFloat(item.weight) || 0) * (parseFloat(item.qty) || 0)), 0)
        : 0;
      const packagingWeightTotal = packing
        ? packages.reduce((sum, pkg) => sum + (parseFloat(pkg.boxWeight) || 0), 0)
        : 0;

      const model = {
        companyKey: currentOffer.company,
        company: {
          name: company.name,
          address: company.address,
          email: company.email,
          banks: company.banks
        },
        offerType,
        offerNumber: currentOffer.offerNumber || '',
        date: currentOffer.date,
        currency: currentOffer.currency,
        description: currentOffer.description || '',
        engineSerialNumber: currentOffer.engineSerialNumber || '',
        customer: currentOffer.customer || {},
        items,
        showPrices: priced || purchaseOrder,
        subtotal,
        discount: discountActive ? {
          label: currentOffer.discountLabel === 'paid' ? 'Already Paid' : 'Discount',
          type: currentOffer.discountType,
          value: currentOffer.discountValue,
          amount: currentOffer.discountAmount
        } : null,
        finalTotal: discountActive ? getFinalTotal() : subtotal,
        poTotals: purchaseOrder ? getPurchaseOrderTotalsByCurrency() : [],
        terms: currentOffer.terms,
        bankDetails: priced ? currentOffer.bankDetails : null,
        showBankOnDocument: priced,
        remarks: showOfferComments ? (offerComments || '') : '',
        weightUnit,
        packages: packageRows,
        weightSummary: packing ? {
          packageCount: packages.length,
          itemsWeight: itemsWeightTotal,
          packagingWeight: packagingWeightTotal,
          grossWeight: itemsWeightTotal + packagingWeightTotal
        } : null,
        shipping: packing ? {
          pickup: pickupAddress || '',
          delivery: deliveryAddress || '',
          consignee: showConsignee ? (consigneeAddress || '') : null
        } : null,
        offerAddresses: !packing && showOfferAddresses ? {
          pickup: offerPickupAddress || '',
          shipping: offerShippingAddress || ''
        } : null,
        logoBase64
      };

      const workbook = buildDocumentWorkbook(model);
      const filename = buildExcelFilename(model);
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(blob, filename);
    } catch (error) {
      console.warn('Excel export failed:', error);
      alert('There was an error exporting the Excel file. Please try again.');
    }
  };

  const currentCompany = companies[currentOffer.company];

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      darkMode 
        ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900' 
        : 'bg-gradient-to-br from-blue-50 via-white to-green-50'
    }`}>
      <div className="max-w-7xl mx-auto p-6">
        {/* Modern Header with Dark Mode Toggle */}
        <div className="text-center mb-8 relative">
          {/* Dark Mode Toggle */}
          <button
            onClick={() => setDarkMode(!darkMode)}
            className={`absolute right-0 top-0 p-3 rounded-xl transition-all ${
              darkMode 
                ? 'bg-gray-700 hover:bg-gray-600 text-yellow-400' 
                : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
            }`}
            title={darkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
          >
            {darkMode ? '☀️' : '🌙'}
          </button>
          
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-600 to-blue-800 rounded-2xl mb-4">
            <span className="text-white text-2xl">🌊</span>
          </div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent mb-2">
            Marine Offer Builder
          </h1>
          <p className={`text-lg ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            Create professional marine equipment offers with ease
          </p>
        </div>

        {/* Company Selection Card */}
        <div className={`rounded-3xl shadow-xl border p-8 mb-8 transition-colors duration-300 ${
          darkMode 
            ? 'bg-gray-800 border-gray-700' 
            : 'bg-white border-gray-100'
        }`}>
          <h2 className={`text-2xl font-semibold mb-6 flex items-center ${
            darkMode ? 'text-gray-100' : 'text-gray-800'
          }`}>
            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
              <span className="text-blue-600 font-bold">🏢</span>
            </div>
            Company Selection
          </h2>
          <select
            value={currentOffer.company}
            onChange={(e) => setCurrentOffer(prev => ({
              ...prev,
              company: e.target.value,
              // Reset bank so the company's first available bank is picked up
              // by the bank-details normalization effect.
              bank: Object.keys(companies[e.target.value]?.banks || {})[0] || ''
            }))}
            className={`w-full p-4 text-lg border-2 rounded-2xl focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all ${
              dm('border-gray-200 bg-white text-gray-900', 'border-gray-600 bg-gray-700 text-gray-100')
            }`}
          >
            <option value="fin-marine">🇲🇭 Fin Marine & Energy Solutions Ltd (Marshall Islands)</option>
            <option value="fin-marine-fzco">🇦🇪 Fin Marine International FZCO (Dubai)</option>
            <option value="atlantis-marine">🇦🇪 Atlantis Marine and Power Solution (RAK)</option>
          </select>

          {/* Bank Selector - only shown when the selected company has more than one bank */}
          {Object.keys(currentCompany?.banks || {}).length > 1 && (
            <div className="mt-6">
              <label className={`block text-sm font-semibold mb-3 ${dm('text-gray-700', 'text-gray-300')}`}>
                Payment Bank
              </label>
              <select
                value={currentOffer.bank}
                onChange={(e) => setCurrentOffer(prev => ({ ...prev, bank: e.target.value }))}
                className={`w-full p-4 text-lg border-2 rounded-2xl focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all ${
                  dm('border-gray-200 bg-white text-gray-900', 'border-gray-600 bg-gray-700 text-gray-100')
                }`}
              >
                {Object.entries(currentCompany.banks).map(([bankKey, bank]) => (
                  <option key={bankKey} value={bankKey}>
                    {bank.label} ({Object.keys(bank.accounts || {}).join(', ')})
                  </option>
                ))}
              </select>
              <p className={`mt-2 text-xs ${dm('text-gray-500', 'text-gray-400')}`}>
                Bank details on the generated PDF / Excel will use this account.
              </p>
            </div>
          )}
        </div>

        {/* Offer Details Card */}
        <div className={`rounded-3xl shadow-xl border p-8 mb-8 transition-colors duration-300 ${
          dm('bg-white border-gray-100', 'bg-gray-800 border-gray-700')
        }`}>
          <h2 className={`text-2xl font-semibold mb-6 flex items-center ${
            dm('text-gray-800', 'text-gray-100')
          }`}>
            <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mr-3">
              <span className="text-green-600 font-bold">📋</span>
            </div>
            Offer Information
          </h2>
          
          {/* Offer Type Selector */}
          <div className="mb-6">
            <label className={`block text-sm font-semibold mb-3 ${dm('text-gray-700', 'text-gray-300')}`}>Document Type</label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {['OFFER', 'PROFORMA INVOICE', 'INVOICE', 'COMMERCIAL INVOICE', 'PACKING LIST', 'PURCHASE ORDER', 'INQUIRY'].map(type => (
                <button
                  key={type}
                  onClick={() => setCurrentOffer(prev => ({
                    ...prev,
                    offerType: type,
                    items: type === 'INQUIRY'
                      ? prev.items.map(item => ({ ...item, availability: '' }))
                      : prev.items
                  }))}
                  className={`py-3 px-4 rounded-xl font-medium transition-all ${
                    currentOffer.offerType === type
                      ? 'bg-blue-600 text-white shadow-lg transform scale-105'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:shadow-md'
                  }`}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div>
              <label className={labelClass}>Offer Number</label>
              <input
                type="text"
                value={currentOffer.offerNumber}
                onChange={(e) => setCurrentOffer(prev => ({ ...prev, offerNumber: e.target.value }))}
                className={inputClass}
                placeholder="Auto-generated"
              />
            </div>
            <div>
                <label className={labelClass}>Date</label>
              <input
                type="date"
                value={currentOffer.date}
                onChange={(e) => setCurrentOffer(prev => ({ ...prev, date: e.target.value }))}
                  className={inputClass}
              />
            </div>
            {currentOffer.offerType !== 'PACKING LIST' && currentOffer.offerType !== 'PURCHASE ORDER' && currentOffer.offerType !== 'INQUIRY' && (
            <div>
                <label className={labelClass}>Currency</label>
              <select
                value={currentOffer.currency}
                onChange={(e) => setCurrentOffer(prev => ({ ...prev, currency: e.target.value }))}
                  className={inputClass}
              >
                {Object.keys(currentCompany?.banks?.[currentOffer.bank]?.accounts || {}).map(currency => (
                  <option key={currency} value={currency}>{currency}</option>
                ))}
              </select>
            </div>
            )}
            {currentOffer.offerType !== 'PACKING LIST' && currentOffer.offerType !== 'PURCHASE ORDER' && currentOffer.offerType !== 'INQUIRY' && (
              <div>
                <label className={labelClass}>Engine Serial No</label>
                <input
                  type="text"
                  value={currentOffer.engineSerialNumber}
                  onChange={(e) => setCurrentOffer(prev => ({ ...prev, engineSerialNumber: e.target.value }))}
                  className={inputClass}
                />
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
            <div>
              <label className={labelClass}>{(currentOffer.offerType === 'PURCHASE ORDER' || currentOffer.offerType === 'INQUIRY') ? 'Supplier Information' : 'Customer Information'}</label>
              <textarea
                value={currentOffer.customer.name}
                onChange={(e) => setCurrentOffer(prev => ({
                  ...prev,
                  customer: { ...prev.customer, name: e.target.value }
                }))}
                className={`${inputClass} resize-none`}
                rows="2"
                placeholder={(currentOffer.offerType === 'PURCHASE ORDER' || currentOffer.offerType === 'INQUIRY') ? 'Supplier name and company details' : 'Customer name and company details'}
              />
            </div>
            <div>
              <label className={labelClass}>Project</label>
              <input
                type="text"
                value={currentOffer.customer.project || ''}
                onChange={(e) => setCurrentOffer(prev => ({
                  ...prev,
                  customer: { ...prev.customer, project: e.target.value }
                }))}
                className={inputClass}
                placeholder="Project name or reference"
              />
            </div>
          </div>
          <div className="mt-6">
            <label className={labelClass}>For (Equipment/Service)</label>
              <textarea
                value={currentOffer.description}
                onChange={(e) => setCurrentOffer(prev => ({ ...prev, description: e.target.value }))}
              className={`${inputClass} resize-none`}
              rows="2"
                placeholder="Brief description of equipment or service"
              />
          </div>
        </div>

        {/* Items Section */}
        <div className={cardClass}>
          <div className="flex justify-between items-center mb-8">
            <h2 className={headingClass}>
              <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center mr-3">
                <span className="text-purple-600 font-bold">📦</span>
              </div>
              Items & Pricing
            </h2>
            <button
              onClick={addItem}
              className="flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-2xl hover:from-blue-700 hover:to-blue-800 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              <Plus size={20} />
              Add Item
            </button>
          </div>

          {/* Packing List Excel Import */}
          {currentOffer.offerType === 'PACKING LIST' && (
            <div className={`mb-8 p-5 rounded-2xl border-2 border-dashed ${
              dm('bg-purple-50 border-purple-200', 'bg-gray-900 border-purple-800')
            }`}>
              <input
                type="file"
                ref={packingFileInputRef}
                onChange={handlePackingFileUpload}
                accept=".xlsx,.xls,.csv"
                className="hidden"
              />
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <h3 className={`font-semibold flex items-center gap-2 ${dm('text-purple-800', 'text-purple-300')}`}>
                    <FileSpreadsheet size={20} />
                    Fill Packing List from Excel
                  </h3>
                  <p className={`text-sm mt-1 ${dm('text-purple-600', 'text-gray-400')}`}>
                    Upload an Excel or CSV file. One sheet per box works best — box names, dimensions and weights are picked up automatically, along with item columns like Description, Part No and Qty. A single sheet with a Package column works too.
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={downloadPackingTemplate}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium border-2 transition-all ${
                      dm('border-purple-300 text-purple-700 hover:bg-purple-100', 'border-purple-700 text-purple-300 hover:bg-gray-800')
                    }`}
                  >
                    <Download size={18} />
                    Template
                  </button>
                  <button
                    onClick={() => packingFileInputRef.current?.click()}
                    disabled={packingImportLoading}
                    className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-xl font-medium hover:from-purple-700 hover:to-purple-800 transition-all shadow-md hover:shadow-lg disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <Upload size={18} />
                    {packingImportLoading ? 'Importing...' : 'Upload File'}
                  </button>
                </div>
              </div>
              {packingImportError && (
                <div className={`mt-4 flex items-center gap-2 p-3 rounded-lg text-sm ${
                  dm('bg-red-50 text-red-700 border border-red-200', 'bg-red-900/30 text-red-300 border border-red-800')
                }`}>
                  <AlertCircle size={16} className="flex-shrink-0" />
                  {packingImportError}
                </div>
              )}
              {packingImportSuccess && (
                <div className={`mt-4 flex items-center gap-2 p-3 rounded-lg text-sm ${
                  dm('bg-green-50 text-green-700 border border-green-200', 'bg-green-900/30 text-green-300 border border-green-800')
                }`}>
                  <FileSpreadsheet size={16} className="flex-shrink-0" />
                  {packingImportSuccess}
                </div>
              )}
            </div>
          )}

          {/* Items List */}
          <div className="space-y-5">
                {currentOffer.items.map((item, index) => (
              <div key={item.id} className={`rounded-xl p-5 border-2 transition-all ${
                dm('bg-gray-50 border-gray-100 hover:border-gray-200', 'bg-gray-900 border-gray-700 hover:border-gray-600')
              }`}>
                {/* Item Header */}
                <div className="flex justify-between items-center mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="font-bold text-blue-600 text-sm">{index + 1}</span>
                    </div>
                    <h3 className={`text-base font-semibold ${dm('text-gray-700', 'text-gray-300')}`}>Item {index + 1}</h3>
                  </div>
                  <button
                    onClick={() => removeItem(index)}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50 p-2 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={currentOffer.items.length === 1}
                  >
                    <Trash2 size={18} />
                  </button>
                </div>

                {/* Item Fields Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-4">
                  {/* Description - Full Width */}
                  <div className="lg:col-span-2 xl:col-span-4">
                    <label className={`block text-sm font-medium mb-1 ${dm('text-gray-700', 'text-gray-300')}`}>Description</label>
                      <textarea
                        value={item.description}
                        onChange={(e) => updateItem(index, 'description', e.target.value)}
                      className={`${itemInputClass} resize-none`}
                      rows="2"
                        placeholder="Detailed item description"
                      />
                  </div>

                  {/* Part Number */}
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${dm('text-gray-700', 'text-gray-300')}`}>Part No</label>
                      <input
                        type="text"
                        value={item.partNo}
                        onChange={(e) => updateItem(index, 'partNo', e.target.value)}
                      className={itemInputClass}
                        placeholder="Part number"
                      />
                  </div>

                  {/* Condition */}
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${dm('text-gray-700', 'text-gray-300')}`}>Condition</label>
                      <select
                        value={item.condition}
                        onChange={(e) => updateItem(index, 'condition', e.target.value)}
                      className={`${itemInputClass} cursor-pointer`}
                      >
                        <option value="NEW">NEW</option>
                        <option value="USED">USED</option>
                        <option value="RECONDITIONED">RECONDITIONED</option>
                      </select>
                  </div>

                  {/* Country of Origin (COO) - hidden when doc type doesn't support it */}
                  {isCOOMakeApplicable() && (
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${dm('text-gray-700', 'text-gray-300')}`}>Country of Origin (COO)</label>
                      <select
                        value={item.coo || 'none'}
                        onChange={(e) => updateItem(index, 'coo', e.target.value)}
                        className={`${itemInputClass} cursor-pointer`}
                      >
                        <option value="none">None (hide in PDF)</option>
                        <option value="EU">EU Maker</option>
                        <option value="NON_EU">Non-EU Maker</option>
                        <option value="OTHER">Other (custom)</option>
                      </select>
                      {(item.coo || 'none') === 'OTHER' && (
                        <input
                          type="text"
                          value={item.cooOther || ''}
                          onChange={(e) => updateItem(index, 'cooOther', e.target.value)}
                          className={`${itemInputClass} mt-2`}
                          placeholder="Enter custom country of origin"
                        />
                      )}
                    </div>
                  )}

                  {/* Make - hidden when doc type doesn't support it */}
                  {isCOOMakeApplicable() && (
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${dm('text-gray-700', 'text-gray-300')}`}>Make</label>
                      <select
                        value={item.make || 'none'}
                        onChange={(e) => updateItem(index, 'make', e.target.value)}
                        className={`${itemInputClass} cursor-pointer`}
                      >
                        <option value="none">None (hide in PDF)</option>
                        <option value="ALT">ALT</option>
                        <option value="OEM">OEM</option>
                        <option value="ALT-OEM">ALT-OEM</option>
                        <option value="OTHER">Other (custom)</option>
                      </select>
                      {(item.make || 'none') === 'OTHER' && (
                        <input
                          type="text"
                          value={item.makeOther || ''}
                          onChange={(e) => updateItem(index, 'makeOther', e.target.value)}
                          className={`${itemInputClass} mt-2`}
                          placeholder="Enter custom make"
                        />
                      )}
                    </div>
                  )}

                  {/* Quantity */}
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${dm('text-gray-700', 'text-gray-300')}`}>Qty</label>
                      <input
                        type="number"
                        value={item.qty}
                        onChange={(e) => updateItem(index, 'qty', parseFloat(e.target.value) || 0)}
                      className={`${itemInputClass} text-center font-semibold`}
                        min="0"
                        step="1"
                      placeholder="0"
                    />
                  </div>

                  {/* Weight (for Packing List) */}
                  {currentOffer.offerType === 'PACKING LIST' && (
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${dm('text-gray-700', 'text-gray-300')}`}>Weight (KG)</label>
                      <input
                        type="number"
                        value={item.weight || 0}
                        onChange={(e) => updateItem(index, 'weight', parseFloat(e.target.value) || 0)}
                        className={`${itemInputClass} text-center font-semibold`}
                        min="0"
                        step="0.01"
                        placeholder="0.00"
                      />
                    </div>
                  )}

                  {/* Package Assignment (for Packing List) */}
                  {currentOffer.offerType === 'PACKING LIST' && (
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${dm('text-gray-700', 'text-gray-300')}`}>Package</label>
                      <select
                        value={item.packageNumber || 'Box 1'}
                        onChange={(e) => updateItem(index, 'packageNumber', e.target.value)}
                        className={itemInputClass}
                      >
                        {/* Options come from the actual packages list; keep the item's
                            current value selectable even if its package was removed */}
                        {packages.map(pkg => (
                          <option key={pkg.id} value={pkg.number}>{pkg.number}</option>
                        ))}
                        {item.packageNumber && !packages.some(pkg => pkg.number === item.packageNumber) && (
                          <option value={item.packageNumber}>{item.packageNumber}</option>
                        )}
                      </select>
                    </div>
                  )}

                  {/* Currency (for Purchase Order) */}
                  {currentOffer.offerType === 'PURCHASE ORDER' && (
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${dm('text-gray-700', 'text-gray-300')}`}>Currency</label>
                    <select
                      value={item.currency || 'USD'}
                      onChange={(e) => updateItem(index, 'currency', e.target.value)}
                      className={`${itemInputClass} cursor-pointer`}
                    >
                      <option value="AED">AED</option>
                      <option value="EUR">EUR</option>
                      <option value="USD">USD</option>
                    </select>
                  </div>
                  )}

                  {/* Unit Price */}
                  {currentOffer.offerType !== 'PACKING LIST' && currentOffer.offerType !== 'INQUIRY' && (
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${dm('text-gray-700', 'text-gray-300')}`}>Unit Price</label>
                    <div className="relative">
                      <span className={`absolute left-3 top-1/2 transform -translate-y-1/2 text-sm font-medium ${dm('text-gray-500', 'text-gray-400')}`}>
                      {currentOffer.offerType === 'PURCHASE ORDER' ? (item.currency || 'USD') : currentOffer.currency}
                      </span>
                      <input
                        type="text"
                        value={formatPrice(item.unitPrice)}
                        onChange={(e) => {
                          const input = e.target;
                          const oldValue = input.value;
                          const cursorPos = input.selectionStart;
                          
                          // Count digits before cursor in the old value
                          let digitsBeforeCursor = 0;
                          for (let i = 0; i < cursorPos; i++) {
                            if (/\d/.test(oldValue[i])) {
                              digitsBeforeCursor++;
                            }
                          }
                          
                          // Remove all non-numeric characters except decimal point
                          const cleanValue = e.target.value.replace(/[^\d.]/g, '');
                          // Ensure only one decimal point
                          const parts = cleanValue.split('.');
                          const formattedValue = parts[0] + (parts.length > 1 ? '.' + parts.slice(1).join('') : '');
                          // Parse and round to avoid floating point issues
                          const numValue = parseFloat(formattedValue) || 0;
                          const roundedValue = Math.round(numValue * 100) / 100;
                          updateItem(index, 'unitPrice', roundedValue);
                          
                          // After React re-renders with the new formatted value, restore cursor position
                          setTimeout(() => {
                            const newValue = formatPrice(roundedValue);
                            let newCursorPos = 0;
                            let digitsSeen = 0;
                            
                            // Find position after the same number of digits
                            for (let i = 0; i < newValue.length; i++) {
                              if (/\d/.test(newValue[i])) {
                                digitsSeen++;
                                if (digitsSeen === digitsBeforeCursor) {
                                  newCursorPos = i + 1;
                                  break;
                                }
                              }
                            }
                            
                            // If we haven't seen enough digits, put cursor at the end before decimal
                            if (digitsSeen < digitsBeforeCursor) {
                              const decimalIndex = newValue.indexOf('.');
                              newCursorPos = decimalIndex > -1 ? decimalIndex : newValue.length;
                            }
                            
                            input.setSelectionRange(newCursorPos, newCursorPos);
                          }, 0);
                        }}
                        onFocus={(e) => {
                          // Show number without thousand separators for easier editing
                          const numValue = parseFloat(item.unitPrice) || 0;
                          e.target.value = numValue.toFixed(2);
                          // Select all text for easy replacement
                          e.target.select();
                        }}
                        onBlur={(e) => {
                          // Ensure proper formatting on blur
                          const numValue = parseFloat(item.unitPrice) || 0;
                          const roundedValue = Math.round(numValue * 100) / 100;
                          updateItem(index, 'unitPrice', roundedValue);
                        }}
                        className={`${itemInputClass} pl-12 text-right font-medium`}
                        placeholder="0.00"
                      />
                    </div>
                    {isMultiplierApplicable() && getMultiplierPercent() > 0 && (
                      <div className={`text-xs mt-1 ${dm('text-teal-700', 'text-teal-300')}`}>
                        Effective unit price: {currentOffer.offerType === 'PURCHASE ORDER' ? (item.currency || 'USD') : currentOffer.currency} {formatPrice(getAdjustedUnitPrice(item))} (includes +{formatPrice(getMultiplierPercent())}%)
                      </div>
                    )}
                  </div>
                  )}

                  {/* Total Price */}
                  {currentOffer.offerType !== 'PACKING LIST' && currentOffer.offerType !== 'INQUIRY' && (
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${dm('text-gray-700', 'text-gray-300')}`}>Total Price</label>
                    <div className={`border-2 rounded-lg p-3 text-center ${
                      dm('bg-green-50 border-green-200', 'bg-gray-800 border-green-700')
                    }`}>
                      <div className={`font-bold text-lg ${dm('text-green-600', 'text-green-400')}`}>
                      {currentOffer.offerType === 'PURCHASE ORDER' ? (item.currency || 'USD') : currentOffer.currency} {formatPrice(currentOffer.offerType === 'PURCHASE ORDER' ? item.totalPrice : getAdjustedTotalPrice(item))}
                      </div>
                    </div>
                  </div>
                  )}

                  {/* Availability - not applicable to packing lists */}
                  {currentOffer.offerType !== 'PACKING LIST' && (
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${dm('text-gray-700', 'text-gray-300')}`}>Availability</label>
                      <input
                        type="text"
                        value={currentOffer.offerType === 'INQUIRY' ? '' : item.availability}
                        onChange={(e) => {
                          if (currentOffer.offerType === 'INQUIRY') return;
                          updateItem(index, 'availability', e.target.value);
                        }}
                        disabled={currentOffer.offerType === 'INQUIRY'}
                      className={itemInputClass}
                      placeholder={currentOffer.offerType === 'INQUIRY' ? '' : 'Ex works - Subject unsold'}
                    />
                  </div>
                  )}

                  {/* Remarks - Full Width */}
                  <div className="lg:col-span-2 xl:col-span-2">
                    <label className={`block text-sm font-medium mb-1 ${dm('text-gray-700', 'text-gray-300')}`}>Remarks</label>
                    <textarea
                      value={item.remarks}
                      onChange={(e) => updateItem(index, 'remarks', e.target.value)}
                      className={`${itemInputClass} resize-none`}
                      rows="2"
                      placeholder="Additional remarks or notes"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 flex justify-center">
            <button
              onClick={addItem}
              className="flex items-center gap-2 px-5 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all shadow-md hover:shadow-lg"
            >
              <Plus size={18} />
              Add More Items
            </button>
          </div>

          {/* Package Details Section (for Packing List) */}
          {currentOffer.offerType === 'PACKING LIST' && (
            <div className={`mt-8 ${cardClass}`}>
              <h2 className={headingClass}>
                <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center mr-3">
                  <span className="text-purple-600 font-bold">📦</span>
                </div>
                Package Details
              </h2>
              
              {/* Package Summary */}
              <div className="mb-6">
                <h3 className={`text-lg font-semibold mb-4 ${dm('text-gray-800', 'text-gray-200')}`}>
                  Total Packages: {packages.length}
                </h3>
                <div className="space-y-4">
                  {packages.map((pkg, index) => {
                    // Calculate weight for this package
                    const packageWeight = currentOffer.items
                      .filter(item => item.packageNumber === pkg.number)
                      .reduce((sum, item) => sum + (parseFloat(item.weight || 0) * parseFloat(item.qty || 0)), 0);
                    
                    return (
                      <div key={pkg.id} className={`p-4 rounded-lg border ${dm('bg-gray-50 border-gray-200', 'bg-gray-800 border-gray-700')}`}>
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h4 className={`font-semibold mb-2 ${dm('text-gray-800', 'text-gray-200')}`}>
                              {pkg.number}
                            </h4>
                            <div className="space-y-4">
                              <div>
                                <label className={`block text-sm font-medium mb-1 ${dm('text-gray-600', 'text-gray-400')}`}>Type</label>
                                <select
                                  value={pkg.type}
                                  onChange={(e) => {
                                    const newPackages = [...packages];
                                    newPackages[index].type = e.target.value;
                                    setPackages(newPackages);
                                  }}
                                  className={inputClass}
                                >
                                  {PACKAGE_TYPE_OPTIONS.map(option => (
                                    <option key={option} value={option}>{option}</option>
                                  ))}
                                </select>
                              </div>
                              <div>
                                <label className={`block text-sm font-medium mb-1 ${dm('text-gray-600', 'text-gray-400')}`}>
                                  Dimensions & Weight
                                </label>
                                <div className="grid grid-cols-4 gap-2">
                                  <div>
                                    <input
                                      type="number"
                                      value={pkg.length}
                                      onChange={(e) => {
                                        const newPackages = [...packages];
                                        newPackages[index].length = parseFloat(e.target.value) || 0;
                                        setPackages(newPackages);
                                      }}
                                      className={`${inputClass} text-center`}
                                      placeholder="Length"
                                      min="0"
                                      step="0.1"
                                    />
                                    <span className={`text-xs ${dm('text-gray-500', 'text-gray-400')} block text-center mt-1`}>Length (cm)</span>
                                  </div>
                                  <div>
                                    <input
                                      type="number"
                                      value={pkg.width}
                                      onChange={(e) => {
                                        const newPackages = [...packages];
                                        newPackages[index].width = parseFloat(e.target.value) || 0;
                                        setPackages(newPackages);
                                      }}
                                      className={`${inputClass} text-center`}
                                      placeholder="Width"
                                      min="0"
                                      step="0.1"
                                    />
                                    <span className={`text-xs ${dm('text-gray-500', 'text-gray-400')} block text-center mt-1`}>Width (cm)</span>
                                  </div>
                                  <div>
                                    <input
                                      type="number"
                                      value={pkg.height}
                                      onChange={(e) => {
                                        const newPackages = [...packages];
                                        newPackages[index].height = parseFloat(e.target.value) || 0;
                                        setPackages(newPackages);
                                      }}
                                      className={`${inputClass} text-center`}
                                      placeholder="Height"
                                      min="0"
                                      step="0.1"
                                    />
                                    <span className={`text-xs ${dm('text-gray-500', 'text-gray-400')} block text-center mt-1`}>Height (cm)</span>
                                  </div>
                                  <div>
                                    <input
                                      type="number"
                                      value={pkg.boxWeight}
                                      onChange={(e) => {
                                        const newPackages = [...packages];
                                        newPackages[index].boxWeight = parseFloat(e.target.value) || 0;
                                        setPackages(newPackages);
                                      }}
                                      className={`${inputClass} text-center`}
                                      placeholder="Box weight"
                                      min="0"
                                      step="0.1"
                                    />
                                    <span className={`text-xs ${dm('text-gray-500', 'text-gray-400')} block text-center mt-1`}>Box Weight ({weightUnit})</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="mt-4 p-3 rounded-lg bg-opacity-50 ${dm('bg-blue-50', 'bg-gray-700')}">
                              <div className={`text-sm ${dm('text-gray-700', 'text-gray-300')}`}>
                                <div className="font-semibold mb-1">
                                  {pkg.number}: {pkg.type}, {pkg.length} × {pkg.width} × {pkg.height} cm, Box weight: {pkg.boxWeight} {weightUnit}
                                </div>
                                <div className="ml-4">
                                  Contains {currentOffer.items.filter(item => item.packageNumber === pkg.number).length} items weighing {packageWeight.toFixed(2)} {weightUnit}
                                </div>
                                <div className="ml-4 font-semibold ${dm('text-blue-700', 'text-blue-400')}">
                                  Total package weight: {(packageWeight + parseFloat(pkg.boxWeight || 0)).toFixed(2)} {weightUnit}
                                </div>
                              </div>
                            </div>
                          </div>
                          {packages.length > 1 && (
                            <button
                              onClick={() => {
                                setPackages(packages.filter(p => p.id !== pkg.id));
                              }}
                              className="ml-4 text-red-500 hover:text-red-700"
                            >
                              <Trash2 size={20} />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                {/* Add Package Button */}
                {packages.length < 10 && (
                  <button
                    onClick={() => {
                      const newPackageNumber = `Box ${packages.length + 1}`;
                      setPackages([...packages, {
                        id: Date.now(),
                        number: newPackageNumber,
                        type: 'Wooden Box',
                        length: 0,
                        width: 0,
                        height: 0,
                        boxWeight: 0
                      }]);
                    }}
                    className={`mt-4 flex items-center gap-2 px-4 py-2 rounded-lg ${
                      dm('bg-purple-500 hover:bg-purple-600 text-white', 
                         'bg-purple-600 hover:bg-purple-700 text-white')
                    }`}
                  >
                    <Plus size={20} />
                    Add Package
                  </button>
                )}
              </div>
              
              {/* Weight Unit Selector */}
              <div className="mt-6 mb-4">
                <label className={`block text-sm font-semibold mb-2 ${dm('text-gray-700', 'text-gray-300')}`}>Weight Unit</label>
                <select
                  value={weightUnit}
                  onChange={(e) => setWeightUnit(e.target.value)}
                  className={`${inputClass} w-48`}
                >
                  <option value="KG">Kilograms (KG)</option>
                  <option value="LBS">Pounds (LBS)</option>
                </select>
              </div>
              
              {/* Weight Summary */}
              <div className={`mt-6 p-4 rounded-lg ${dm('bg-blue-50 border border-blue-200', 'bg-gray-800 border border-gray-700')}`}>
                <h3 className={`text-lg font-semibold mb-2 ${dm('text-blue-800', 'text-blue-400')}`}>
                  Weight Summary
                </h3>
                <div className="space-y-2">
                  <div className={`flex justify-between ${dm('text-gray-700', 'text-gray-300')}`}>
                    <span>Number of packages:</span>
                    <span className="font-semibold">{packages.length}</span>
                  </div>
                  <div className={`flex justify-between ${dm('text-gray-700', 'text-gray-300')}`}>
                    <span>Total items weight:</span>
                    <span className="font-semibold">{currentOffer.items.reduce((sum, item) => sum + (parseFloat(item.weight || 0) * parseFloat(item.qty || 0)), 0).toFixed(2)} {weightUnit}</span>
                  </div>
                  <div className={`flex justify-between ${dm('text-gray-700', 'text-gray-300')}`}>
                    <span>Total packaging weight:</span>
                    <span className="font-semibold">{packages.reduce((sum, pkg) => sum + parseFloat(pkg.boxWeight || 0), 0).toFixed(2)} {weightUnit}</span>
                  </div>
                  <div className={`pt-2 mt-2 border-t ${dm('border-blue-200', 'border-gray-600')}`}>
                    <div className={`flex justify-between font-bold text-lg ${dm('text-blue-800', 'text-blue-400')}`}>
                      <span>Total gross weight:</span>
                      <span>{(currentOffer.items.reduce((sum, item) => sum + (parseFloat(item.weight || 0) * parseFloat(item.qty || 0)), 0) + packages.reduce((sum, pkg) => sum + parseFloat(pkg.boxWeight || 0), 0)).toFixed(2)} {weightUnit}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Address Sections (for Packing List) */}
          {currentOffer.offerType === 'PACKING LIST' && (
            <div>
              {/* Toggle for Consignee Address */}
              <div className="mt-6 mb-4">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showConsignee}
                    onChange={(e) => setShowConsignee(e.target.checked)}
                    className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                  />
                  <span className={`ml-3 text-sm font-semibold ${dm('text-gray-700', 'text-gray-300')}`}>
                    Include Consignee Address
                  </span>
                </label>
              </div>
              
              <div className={`mt-8 grid grid-cols-1 ${showConsignee ? 'md:grid-cols-3' : 'md:grid-cols-2'} gap-8`}>
                {/* Pickup Address */}
                <div className={cardClass}>
                  <h2 className={headingClass}>
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-blue-600 font-bold">🚚</span>
                    </div>
                    Pickup Address
                  </h2>
                  <textarea
                    value={pickupAddress}
                    onChange={(e) => setPickupAddress(e.target.value)}
                    rows="4"
                    placeholder="Enter complete pickup address"
                    className={`w-full p-3 rounded-lg border ${
                      dm('border-gray-300 bg-white', 'border-gray-600 bg-gray-800')
                    } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                  />
                </div>
                
                {/* Consignee Address (conditional) */}
                {showConsignee && (
                  <div className={cardClass}>
                    <h2 className={headingClass}>
                      <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center mr-3">
                        <span className="text-purple-600 font-bold">👤</span>
                      </div>
                      Consignee Address
                    </h2>
                    <textarea
                      value={consigneeAddress}
                      onChange={(e) => setConsigneeAddress(e.target.value)}
                      rows="4"
                      placeholder="Enter consignee address"
                      className={`w-full p-3 rounded-lg border ${
                        dm('border-gray-300 bg-white', 'border-gray-600 bg-gray-800')
                      } focus:ring-2 focus:ring-purple-500 focus:border-transparent`}
                    />
                  </div>
                )}
                
                {/* Delivery Address */}
                <div className={cardClass}>
                  <h2 className={headingClass}>
                    <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-green-600 font-bold">📍</span>
                    </div>
                    Delivery Address
                  </h2>
                  <textarea
                    value={deliveryAddress}
                    onChange={(e) => setDeliveryAddress(e.target.value)}
                    rows="4"
                    placeholder="Enter complete delivery address"
                    className={`w-full p-3 rounded-lg border ${
                      dm('border-gray-300 bg-white', 'border-gray-600 bg-gray-800')
                    } focus:ring-2 focus:ring-green-500 focus:border-transparent`}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Purchase Order Totals — one card per currency */}
          {currentOffer.offerType === 'PURCHASE ORDER' && currentOffer.items?.length > 0 && (
            <div className={`mt-8 rounded-2xl p-6 border-2 ${
              dm('bg-gradient-to-r from-green-50 to-green-100 border-green-200',
                 'bg-gradient-to-r from-gray-800 to-gray-900 border-green-800')
            }`}>
              <h3 className={`text-xl font-bold mb-4 ${dm('text-green-800', 'text-green-400')}`}>
                PURCHASE ORDER TOTALS
              </h3>
              <p className={`text-sm mb-4 ${dm('text-green-700', 'text-green-300')}`}>
                Each item carries its own currency, so totals are grouped by currency.
              </p>
              <div className="space-y-3">
                {getPurchaseOrderTotalsByCurrency().map(({ currency, amount }) => (
                  <div
                    key={currency}
                    className="flex justify-between items-center"
                  >
                    <h4 className={`text-lg font-semibold ${dm('text-green-800', 'text-green-400')}`}>
                      Total ({currency}):
                    </h4>
                    <div className={`px-6 py-3 rounded-xl shadow-md ${dm('bg-white', 'bg-gray-800')}`}>
                      <div className={`text-2xl font-bold ${dm('text-green-600', 'text-white')}`}>
                        {currency} {formatPrice(amount)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Grand Total */}
          {currentOffer.offerType !== 'PACKING LIST' && currentOffer.offerType !== 'PURCHASE ORDER' && currentOffer.offerType !== 'INQUIRY' && (
          <div className={`mt-8 rounded-2xl p-6 border-2 ${
            dm('bg-gradient-to-r from-green-50 to-green-100 border-green-200', 
               'bg-gradient-to-r from-gray-800 to-gray-900 border-green-800')
          }`}>
            <div className="flex justify-between items-center mb-4">
              <h3 className={`text-xl font-bold ${dm('text-green-800', 'text-green-400')}`}>
                    GRAND TOTAL ({currentOffer.currency}):
              </h3>
              <div className={`px-8 py-4 rounded-xl shadow-md ${
                dm('bg-white', 'bg-gray-800')
              }`}>
                <div className={`text-3xl font-bold ${dm('text-green-600', 'text-white')}`}>
                                          {currentOffer.currency} {formatPrice(getSubtotal())}
                </div>
              </div>
            </div>

            {isMultiplierApplicable() && (
              <div className={`p-4 rounded-xl mb-4 ${dm('bg-amber-50 border border-amber-200', 'bg-gray-700 border border-amber-700')}`}>
                <p className={`text-sm font-medium mb-3 ${dm('text-amber-800', 'text-amber-300')}`}>
                  Disclaimer: all item prices will be multiplied by this percentage.
                </p>
                <div className="flex items-center gap-3">
                  <label className={`text-sm font-semibold ${dm('text-gray-700', 'text-gray-300')}`}>Multiplier (%)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={currentOffer.priceMultiplierPercent || 0}
                    onChange={(e) => setCurrentOffer(prev => ({
                      ...prev,
                      priceMultiplierPercent: Math.max(0, parseFloat(e.target.value) || 0)
                    }))}
                    className={`w-32 px-3 py-2 rounded-lg border ${
                      dm('border-gray-300 bg-white text-gray-900', 'border-gray-600 bg-gray-800 text-white')
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setCurrentOffer(prev => ({ ...prev, priceMultiplierPercent: 0 }))}
                    className={`px-3 py-2 rounded-lg border text-sm font-medium ${
                      dm('border-red-200 bg-red-50 text-red-700 hover:bg-red-100', 'border-red-700 bg-gray-800 text-red-300 hover:bg-gray-700')
                    }`}
                  >
                    Remove Multiplier
                  </button>
                  <span className={`text-sm ${dm('text-teal-700', 'text-teal-300')}`}>
                    Unit prices include +{formatPrice(getMultiplierPercent())}%
                  </span>
                </div>
              </div>
            )}

            {/* Discount Toggle */}
            <div className="flex items-center justify-between mb-4">
              <label className={`flex items-center cursor-pointer text-lg font-semibold ${dm('text-gray-700', 'text-gray-300')}`}>
                <input
                  type="checkbox"
                  checked={currentOffer.discountEnabled}
                  onChange={(e) => setCurrentOffer(prev => ({
                    ...prev,
                    discountEnabled: e.target.checked,
                    discountAmount: e.target.checked ? calculateDiscountAmount(getSubtotal(), prev.discountType, prev.discountValue) : 0
                  }))}
                  className="sr-only"
                />
                <div className={`relative w-12 h-6 rounded-full transition-colors duration-200 ${
                  currentOffer.discountEnabled ? 'bg-blue-500' : dm('bg-gray-300', 'bg-gray-600')
                }`}>
                  <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform duration-200 ${
                    currentOffer.discountEnabled ? 'translate-x-6' : 'translate-x-0'
                  }`}></div>
          </div>
                <span className="ml-3">Apply {currentOffer.discountLabel === 'paid' ? 'Already Paid' : 'Discount'}</span>
              </label>
            </div>

            {/* Discount Controls */}
            {currentOffer.discountEnabled && (
              <div className={`p-4 rounded-xl mb-4 ${dm('bg-blue-50 border border-blue-200', 'bg-gray-700 border border-gray-600')}`}>
                <div className="flex flex-wrap gap-4 items-center">
                  {/* Discount Label Toggle */}
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setCurrentOffer(prev => ({ ...prev, discountLabel: 'discount' }))}
                      className={`px-3 py-1 rounded-lg text-sm font-medium transition-all ${
                        currentOffer.discountLabel === 'discount'
                          ? dm('bg-blue-600 text-white', 'bg-blue-500 text-white')
                          : dm('bg-white text-gray-700 border border-gray-300', 'bg-gray-800 text-gray-300 border border-gray-600')
                      }`}
                    >
                      Discount
                    </button>
                    <button
                      type="button"
                      onClick={() => setCurrentOffer(prev => ({ ...prev, discountLabel: 'paid' }))}
                      className={`px-3 py-1 rounded-lg text-sm font-medium transition-all ${
                        currentOffer.discountLabel === 'paid'
                          ? dm('bg-blue-600 text-white', 'bg-blue-500 text-white')
                          : dm('bg-white text-gray-700 border border-gray-300', 'bg-gray-800 text-gray-300 border border-gray-600')
                      }`}
                    >
                      Already Paid
                    </button>
                  </div>

                  {/* Discount Type Selection */}
                  <div className="flex items-center gap-4">
                    <label className={`flex items-center cursor-pointer ${dm('text-gray-700', 'text-gray-300')}`}>
                      <input
                        type="radio"
                        name="discountType"
                        value="percentage"
                        checked={currentOffer.discountType === 'percentage'}
                        onChange={(e) => setCurrentOffer(prev => ({
                          ...prev,
                          discountType: e.target.value,
                          discountAmount: calculateDiscountAmount(getSubtotal(), e.target.value, prev.discountValue)
                        }))}
                        className="mr-2"
                      />
                      Percentage %
                    </label>
                    <label className={`flex items-center cursor-pointer ${dm('text-gray-700', 'text-gray-300')}`}>
                      <input
                        type="radio"
                        name="discountType"
                        value="amount"
                        checked={currentOffer.discountType === 'amount'}
                        onChange={(e) => setCurrentOffer(prev => ({
                          ...prev,
                          discountType: e.target.value,
                          discountAmount: calculateDiscountAmount(getSubtotal(), e.target.value, prev.discountValue)
                        }))}
                        className="mr-2"
                      />
                      Fixed Amount
                    </label>
                  </div>

                  {/* Discount Value Input */}
                  <div className="flex items-center">
                    <div className="relative">
                      {currentOffer.discountType === 'amount' && (
                        <span className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${dm('text-gray-500', 'text-gray-400')}`}>
                          {currentOffer.currency === 'USD' ? '$' : currentOffer.currency}
                        </span>
                      )}
                      <input
                        type="number"
                        min="0"
                        max={currentOffer.discountType === 'percentage' ? 100 : getSubtotal()}
                        step={currentOffer.discountType === 'percentage' ? 1 : 0.01}
                        value={currentOffer.discountValue}
                        onChange={(e) => {
                          const value = parseFloat(e.target.value) || 0;
                          setCurrentOffer(prev => ({
                            ...prev,
                            discountValue: value,
                            discountAmount: calculateDiscountAmount(getSubtotal(), prev.discountType, value)
                          }));
                        }}
                        className={`w-32 px-3 py-2 rounded-lg border ${
                          dm('border-gray-300 bg-white text-gray-900', 'border-gray-600 bg-gray-800 text-white')
                        } ${currentOffer.discountType === 'amount' ? 'pl-8' : ''}`}
                        placeholder="0"
                      />
                      {currentOffer.discountType === 'percentage' && (
                        <span className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${dm('text-gray-500', 'text-gray-400')}`}>
                          %
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Discount Amount Display */}
                {currentOffer.discountValue > 0 && (
                  <div className={`mt-3 text-sm ${dm('text-blue-700', 'text-blue-300')}`}>
                                            {currentOffer.discountLabel === 'paid' ? 'Already paid amount' : 'Discount amount'}: -{currentOffer.currency} {formatPrice(currentOffer.discountAmount)}
                  </div>
                )}
              </div>
            )}

            {/* Final Total Display */}
            {currentOffer.discountEnabled && currentOffer.discountAmount > 0 && (
              <div>
                <div className={`border-t pt-4 ${dm('border-gray-300', 'border-gray-600')}`}>
                  <div className="flex justify-between items-center mb-2">
                    <span className={`text-lg ${dm('text-red-600', 'text-red-400')}`}>
                                              {currentOffer.discountLabel === 'paid' ? 'Already Paid' : 'Discount'} ({currentOffer.discountType === 'percentage' ? `${currentOffer.discountValue}%` : `${currentOffer.currency} ${formatPrice(currentOffer.discountValue)}`}):
                    </span>
                    <span className={`text-lg font-semibold ${dm('text-red-600', 'text-red-400')}`}>
                                              -{currentOffer.currency} {formatPrice(currentOffer.discountAmount)}
                    </span>
                  </div>
                  <div className={`border-t pt-3 ${dm('border-gray-400', 'border-gray-500')}`}>
                    <div className="flex justify-between items-center">
                      <h3 className={`text-xl font-bold ${dm('text-green-800', 'text-green-400')}`}>
                        FINAL TOTAL ({currentOffer.currency}):
                      </h3>
                      <div className={`px-8 py-4 rounded-xl shadow-md ${
                        dm('bg-white', 'bg-gray-800')
                      }`}>
                        <div className={`text-3xl font-bold ${dm('text-green-600', 'text-white')}`}>
                          {currentOffer.currency} {formatPrice(getFinalTotal())}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            </div>
          )}
        </div>

        {/* Terms & Conditions */}
        <div className={cardClass}>
          <h2 className={headingClass}>
            <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center mr-3">
              <span className="text-orange-600 font-bold">📝</span>
            </div>
            Terms & Conditions
          </h2>
          {currentOffer.offerType === 'INQUIRY' ? (
            <div className={`p-4 rounded-xl border ${dm('bg-amber-50 border-amber-200 text-amber-800', 'bg-gray-800 border-amber-700 text-amber-300')}`}>
              Please include your terms and conditions, including delivery time, best price, and payment terms.
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {[
                {
                  field: 'deliveryTerms',
                  label: 'Delivery Terms',
                  placeholder: 'e.g. Ex Works Turkey',
                },
                {
                  field: 'deliveryTime',
                  label: 'Delivery Time',
                  placeholder: 'e.g. 8 weeks after payment',
                },
                {
                  field: 'paymentTerms',
                  label: 'Payment Terms',
                  placeholder: 'e.g. 50% advance payment',
                },
              ].map(({ field, label, placeholder }) => {
                const entries = Array.isArray(currentOffer.terms?.[field])
                  ? currentOffer.terms[field]
                  : [currentOffer.terms?.[field] ?? ''];
                const canRemove = entries.length > 1;
                return (
                  <div key={field} className="flex flex-col">
                    <label className={labelClass}>{label}</label>
                    <div className="flex flex-col gap-3">
                      {entries.map((entry, idx) => (
                        <div
                          key={`${field}-${idx}`}
                          className="flex items-center gap-2"
                        >
                          <input
                            type="text"
                            value={entry}
                            placeholder={placeholder}
                            onChange={(e) =>
                              updateTermsEntry(field, idx, e.target.value)
                            }
                            className={inputClass}
                          />
                          {canRemove && (
                            <button
                              type="button"
                              onClick={() => removeTermsEntry(field, idx)}
                              aria-label={`Remove ${label} entry ${idx + 1}`}
                              title={`Remove ${label} entry ${idx + 1}`}
                              className={`shrink-0 p-3 rounded-2xl border-2 transition-colors ${dm(
                                'border-gray-200 bg-white text-gray-400 hover:text-red-600 hover:border-red-300 hover:bg-red-50',
                                'border-gray-600 bg-gray-700 text-gray-400 hover:text-red-400 hover:border-red-500 hover:bg-red-900/30'
                              )}`}
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                    <button
                      type="button"
                      onClick={() => addTermsEntry(field)}
                      className={`mt-4 self-start inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border-2 border-dashed transition-colors ${dm(
                        'border-orange-200 text-orange-600 hover:bg-orange-50 hover:border-orange-300',
                        'border-orange-700 text-orange-300 hover:bg-orange-900/30 hover:border-orange-500'
                      )}`}
                    >
                      <Plus className="w-4 h-4" />
                      Add More
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Optional Pickup/Shipping Addresses (for non-packing list) */}
        {currentOffer.offerType !== 'PACKING LIST' && currentOffer.offerType !== 'PURCHASE ORDER' && currentOffer.offerType !== 'INQUIRY' && (
          <div className={cardClass}>
            <div className="flex items-center justify-between mb-4">
              <h2 className={headingClass}>
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                  <span className="text-blue-600 font-bold">📍</span>
                </div>
                Shipping Addresses
              </h2>
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={showOfferAddresses}
                  onChange={(e) => setShowOfferAddresses(e.target.checked)}
                  className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                />
                <span className={`ml-2 text-sm font-semibold ${dm('text-gray-700', 'text-gray-300')}`}>
                  Show Addresses
                </span>
              </label>
            </div>
            
            {showOfferAddresses && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className={labelClass}>Pickup Address</label>
                  <textarea
                    value={offerPickupAddress}
                    onChange={(e) => setOfferPickupAddress(e.target.value)}
                    rows="3"
                    placeholder="Enter pickup address"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Shipping Address</label>
                  <textarea
                    value={offerShippingAddress}
                    onChange={(e) => setOfferShippingAddress(e.target.value)}
                    rows="3"
                    placeholder="Enter shipping/delivery address"
                    className={inputClass}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Optional Comments Section (for non-packing list) */}
        {currentOffer.offerType !== 'PACKING LIST' && (
          <div className={cardClass}>
            <div className="flex items-center justify-between mb-4">
              <h2 className={headingClass}>
                <div className="w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center mr-3">
                  <span className="text-yellow-600 font-bold">💬</span>
                </div>
                Comments
              </h2>
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={showOfferComments}
                  onChange={(e) => setShowOfferComments(e.target.checked)}
                  className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                />
                <span className={`ml-2 text-sm font-semibold ${dm('text-gray-700', 'text-gray-300')}`}>
                  Show Comments
                </span>
              </label>
            </div>
            
            {showOfferComments && (
              <textarea
                value={offerComments}
                onChange={(e) => setOfferComments(e.target.value)}
                rows="4"
                placeholder="Enter any additional comments or notes"
                className={inputClass}
              />
            )}
          </div>
        )}

        {/* Bank Details */}
        {currentOffer.offerType !== 'PACKING LIST' && currentOffer.offerType !== 'PURCHASE ORDER' && currentOffer.offerType !== 'INQUIRY' && (
        <div className={cardClass}>
          <h2 className={headingClass}>
            <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mr-3">
              <span className="text-green-600 font-bold">🏦</span>
            </div>
            Bank Details{currentCompany?.banks?.[currentOffer.bank]?.label ? ` - ${currentCompany.banks[currentOffer.bank].label}` : ''} ({currentOffer.currency} Account)
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label className={`block text-sm font-semibold mb-2 ${dm('text-gray-500', 'text-gray-400')}`}>Account Holder</label>
                <div className={`p-4 rounded-2xl border-2 font-medium ${
                  dm('bg-gray-50 border-gray-200 text-gray-700', 'bg-gray-900 border-gray-700 text-gray-300')
                }`}>
                  {currentOffer.bankDetails.holder}
                </div>
              </div>
              <div>
                <label className={`block text-sm font-semibold mb-2 ${dm('text-gray-500', 'text-gray-400')}`}>Bank</label>
                <div className={`p-4 rounded-2xl border-2 font-medium ${
                  dm('bg-gray-50 border-gray-200 text-gray-700', 'bg-gray-900 border-gray-700 text-gray-300')
                }`}>
                  {currentOffer.bankDetails.bank}
                </div>
              </div>
              <div>
                <label className={`block text-sm font-semibold mb-2 ${dm('text-gray-500', 'text-gray-400')}`}>Branch</label>
                <div className={`p-4 rounded-2xl border-2 font-medium ${
                  dm('bg-gray-50 border-gray-200 text-gray-700', 'bg-gray-900 border-gray-700 text-gray-300')
                }`}>
                  {currentOffer.bankDetails.branch}
                </div>
              </div>
            </div>
            <div className="space-y-4">
              {currentOffer.bankDetails.accountNo && (
                <div>
                  <label className={`block text-sm font-semibold mb-2 ${dm('text-gray-500', 'text-gray-400')}`}>Account No</label>
                  <div className={`p-4 rounded-2xl border-2 font-medium ${
                    dm('bg-gray-50 border-gray-200 text-gray-700', 'bg-gray-900 border-gray-700 text-gray-300')
                  }`}>
                    {currentOffer.bankDetails.accountNo}
                  </div>
                </div>
              )}
              {currentOffer.bankDetails.branchCode && (
                <div>
                  <label className={`block text-sm font-semibold mb-2 ${dm('text-gray-500', 'text-gray-400')}`}>Branch Code</label>
                  <div className={`p-4 rounded-2xl border-2 font-medium ${
                    dm('bg-gray-50 border-gray-200 text-gray-700', 'bg-gray-900 border-gray-700 text-gray-300')
                  }`}>
                    {currentOffer.bankDetails.branchCode}
                  </div>
                </div>
              )}
              <div>
                <label className={`block text-sm font-semibold mb-2 ${dm('text-gray-500', 'text-gray-400')}`}>IBAN</label>
                <div className={`p-4 rounded-2xl border-2 font-bold ${
                  dm('bg-blue-50 border-blue-200 text-blue-700', 'bg-gray-900 border-blue-700 text-blue-400')
                }`}>
                  {currentOffer.bankDetails.iban}
                </div>
              </div>
              <div>
                <label className={`block text-sm font-semibold mb-2 ${dm('text-gray-500', 'text-gray-400')}`}>Swift Code</label>
                <div className={`p-4 rounded-2xl border-2 font-bold ${
                  dm('bg-green-50 border-green-200 text-green-700', 'bg-gray-900 border-green-700 text-green-400')
                }`}>
                  {currentOffer.bankDetails.swiftCode}
                </div>
              </div>
              {currentOffer.bankDetails.routingCode && (
                <div>
                  <label className={`block text-sm font-semibold mb-2 ${dm('text-gray-500', 'text-gray-400')}`}>Routing Code</label>
                  <div className={`p-4 rounded-2xl border-2 font-bold ${
                    dm('bg-purple-50 border-purple-200 text-purple-700', 'bg-gray-900 border-purple-700 text-purple-400')
                  }`}>
                    {currentOffer.bankDetails.routingCode}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        )}

        {/* Export Actions */}
        <div className={cardClass.replace('mb-8', '')}>
          <h2 className={headingClass}>
            <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center mr-3">
              <span className="text-red-600 font-bold">📄</span>
            </div>
            Export Your Offer
          </h2>
          <div className="flex flex-col sm:flex-row justify-center gap-6">
            <button
              onClick={generateProfessionalPDF}
              className="flex items-center justify-center gap-4 px-8 py-6 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-2xl hover:from-red-600 hover:to-red-700 transition-all shadow-xl hover:shadow-2xl transform hover:-translate-y-1 text-lg font-semibold"
            >
              <FileText size={28} />
              <span>Export as PDF</span>
            </button>
            
            <button
              type="button"
              onClick={downloadExcel}
              className="flex items-center justify-center gap-4 px-8 py-6 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-2xl hover:from-green-600 hover:to-green-700 transition-all shadow-xl hover:shadow-2xl transform hover:-translate-y-1 text-lg font-semibold"
            >
              <FileSpreadsheet size={28} />
              <span>Export as Excel</span>
            </button>

            <button
              onClick={onSendToFinanceClick}
              disabled={!canSendToFinance}
              className={`flex items-center justify-center gap-4 px-8 py-6 rounded-2xl transition-all shadow-xl text-lg font-semibold ${
                canSendToFinance
                  ? 'bg-gradient-to-r from-teal-600 to-teal-700 text-white hover:from-teal-700 hover:to-teal-800 hover:shadow-2xl transform hover:-translate-y-1'
                  : 'bg-gray-300 text-gray-600 cursor-not-allowed'
              }`}
            >
              <span>Send to Finance</span>
            </button>
          </div>

          <div className={`text-center mt-6 p-6 rounded-2xl border ${
            dm('text-gray-600 bg-gradient-to-r from-blue-50 to-green-50 border-gray-200', 
               'text-gray-300 bg-gradient-to-r from-gray-800 to-gray-900 border-gray-700')
          }`}>
            <p className="text-lg font-medium">📄 PDF: Opens new window with styled offer → Print to save as PDF</p>
            <p className="text-lg font-medium mt-2">📊 Excel: Downloads a professional .xlsx file with all offer data</p>
          </div>
        </div>
      </div>
    </div>
  );
});

MarineOfferBuilder.displayName = 'MarineOfferBuilder';

export default MarineOfferBuilder;